import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  Divider,
  CircularProgress,
  alpha,
  Chip,
  Tooltip as MuiTooltip,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Skeleton,
  Menu,
  MenuItem,
  ButtonGroup,
  Grow,
} from '@mui/material';
import { 
  Edit as EditIcon,
  BarChart as BarChartIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Flag as FlagIcon,
  Call as CallIcon,
  UploadFile as UploadFileIcon,
  Assignment as AssignmentIcon,
  DeleteOutline as DeleteOutlineIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarTodayIcon,
  FileDownload as FileDownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleBadgeIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Cell
} from 'recharts';
import { Balloons } from '../ui/balloons';
import * as XLSX from 'xlsx';

// Modern scrollbar styles
const modernScrollbarStyles = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme => alpha(theme.palette.primary.main, 0.2),
    borderRadius: '20px',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
    transition: 'background 0.2s ease',
    '&:hover': {
      background: theme => alpha(theme.palette.primary.main, 0.3),
    }
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    border: 'none',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
};

// Modern table text styles
const modernTableStyles = {
  fontSize: '0.95rem',
  fontWeight: 400,
  lineHeight: 1.4,
};



// Utility functions
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

// Get ISO week number and ISO week year (the year that contains the Thursday of the week)
const getWeekInfo = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  
  // Find Thursday of the week (ISO week year is the year of the Thursday)
  // Thursday is day 4 (0 = Sunday, 1 = Monday, ..., 4 = Thursday, ..., 6 = Saturday)
  const thursday = new Date(d);
  const dayOfWeek = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Calculate days to Thursday: 4 - dayOfWeek
  // Sunday (0): +4, Monday (1): +3, Tuesday (2): +2, Wednesday (3): +1,
  // Thursday (4): 0, Friday (5): -1, Saturday (6): -2
  const daysToThursday = 4 - dayOfWeek;
  thursday.setDate(d.getDate() + daysToThursday);
  
  const weekYear = thursday.getFullYear();
  const weekNum = getWeekNumber(d);
  
  return { year: weekYear, week: weekNum };
};

const getWeekDates = (weekYear, weekNum) => {
  const simple = new Date(weekYear, 0, 1 + (weekNum - 1) * 7);
  const day = simple.getDay();
  const mondayDiff = simple.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(simple.setDate(mondayDiff));
  const tuesday = new Date(monday); tuesday.setDate(monday.getDate() + 1);
  const wednesday = new Date(monday); wednesday.setDate(monday.getDate() + 2);
  const thursday = new Date(monday); thursday.setDate(monday.getDate() + 3);
  const friday = new Date(monday); friday.setDate(monday.getDate() + 4);
  
  return {
    startDate: monday,
    endDate: friday,
    dates: {
      monday,
      tuesday,
      wednesday,
      thursday,
      friday
    }
  };
};

const getCurrentWeekInfo = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ...
  
  // On Mondays, we present data for the previous week since the final data for 
  // Friday of the previous week is being reviewed. This keeps Monday as part of
  // the previous week's reporting cycle.
  if (dayOfWeek === 1) {
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    return getWeekInfo(lastWeek);
  }
  
  // On Tuesday through Sunday, show the current week
  // Tuesday marks the start of focusing on the new week's data
  return getWeekInfo(today);
};

const WeeklyStatistics = () => {
  const { currentUser, user } = useAuth();
  
  // Fix for userRole being undefined - extract from user object if available
  const userRole = user?.role || 'admin'; // Temporarily default to admin for development
  
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeekInfo());
  const [weekData, setWeekData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [formData, setFormData] = useState({
    telephonyStats: {
      monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
      tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
      wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
      thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
      friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
    },
    ticketStats: {
      monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
      tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
      wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
      thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
      friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
    },
    weeklyGoals: [], // Changed from weeklyGoal: '' to an array of goal objects
  });
  const [viewTab, setViewTab] = useState(0);
  const [historicalData, setHistoricalData] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hasLaunchedBalloons, setHasLaunchedBalloons] = useState(false);
  const [celebrationEmojis, setCelebrationEmojis] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [saveStatusMessage, setSaveStatusMessage] = useState('');
  const [saveStatusOpen, setSaveStatusOpen] = useState(false); // controls Grow exit animation
  const [previousWeekData, setPreviousWeekData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    telephony: true,
    tickets: true,
  });
  
  // Week menu state
  const [weekMenuAnchorEl, setWeekMenuAnchorEl] = useState(null);
  const isWeekMenuOpen = Boolean(weekMenuAnchorEl);
  const handleOpenWeekMenu = (event) => setWeekMenuAnchorEl(event.currentTarget);
  const handleCloseWeekMenu = () => setWeekMenuAnchorEl(null);
  const handleSelectWeek = (year, week) => {
    setSelectedWeek({ year, week });
    handleCloseWeekMenu();
  };
  const handleGoToCurrentWeek = () => {
    const current = getCurrentWeekInfo();
    setSelectedWeek({ year: current.year, week: current.week });
  };
  
  // No overflow actions; use segmented controls instead
  
  // Historical view toggle controls
  const [showAllWeeks, setShowAllWeeks] = useState(true);
  const [weeksToShow, setWeeksToShow] = useState(12);
  
  // Use useMemo to recalculate weekDates when selectedWeek changes
  const weekDates = useMemo(() => {
    return getWeekDates(selectedWeek.year, selectedWeek.week);
  }, [selectedWeek]);

  // Filter historical data based on toggle settings
  const filteredHistoricalData = useMemo(() => {
    if (showAllWeeks || historicalData.length === 0) {
      return historicalData;
    }

    // Get current week info for reference
    const currentWeekInfo = getCurrentWeekInfo();
    
    // Filter to show only the specified number of recent weeks including current week
    const filtered = historicalData.filter((data) => {
      const dataYear = data.year;
      const dataWeek = parseInt(data.week.substring(1));
      
      // Calculate difference in weeks from current week
      let weekDiff = 0;
      if (dataYear === currentWeekInfo.year) {
        weekDiff = currentWeekInfo.week - dataWeek;
      } else if (dataYear < currentWeekInfo.year) {
        // Approximate calculation for weeks across years
        const weeksInPreviousYear = 52; // Approximate
        weekDiff = (currentWeekInfo.year - dataYear) * weeksInPreviousYear + currentWeekInfo.week - dataWeek;
      } else {
        // Future weeks (shouldn't happen in normal use)
        weekDiff = -1;
      }
      
      // Include current week and the specified number of weeks before
      return weekDiff >= 0 && weekDiff < weeksToShow;
    });

    return filtered;
  }, [historicalData, showAllWeeks, weeksToShow]);
  
  const canEdit = userRole === 'admin' || userRole === 'editor' || userRole === 'SOS';
  
  // Move fetchAvailableWeeks outside of useEffect to make it reusable
  const fetchAvailableWeeks = async () => {
    try {
      // Use a simple query without orderBy to avoid index requirements
      const simpleQuery = query(
        collection(db, 'weekly_statistics')
      );
      
      const querySnapshot = await getDocs(simpleQuery);
      const weeks = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        weeks.push({
          id: doc.id,
          year: data.year,
          week: data.week
        });
      });
      
      // Sort manually since we can't use orderBy
      weeks.sort((a, b) => {
        if (a.year !== b.year) {
          return b.year - a.year; // Descending by year
        }
        return b.week - a.week; // Descending by week
      });
      
      setAvailableWeeks(weeks);
      setError(null);
    } catch (err) {
      console.error('Error fetching available weeks:', err);
      // Don't show error for empty collection - this is expected when first using the feature
      if (err.code === 'permission-denied') {
        setError('Permission denied: You do not have access to statistics data');
      } else {
        // Just log the error but don't show to user - empty collection is expected initially
        console.log('Note: No existing weekly statistics found. This is normal for first use.');
        setError(null);
      }
    }
  };

  // Use fetchAvailableWeeks in useEffect
  useEffect(() => {
    fetchAvailableWeeks();
  }, []);
  
  // Fetch selected week data
  useEffect(() => {
    const fetchWeekData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const weekQuery = query(
          collection(db, 'weekly_statistics'),
          where('year', '==', selectedWeek.year),
          where('week', '==', selectedWeek.week)
        );
        
        const querySnapshot = await getDocs(weekQuery);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          setWeekData({
            id: querySnapshot.docs[0].id,
            ...docData
          });
          
          // Handle data migration: convert old format to new format if needed
          let weeklyGoals = docData.weeklyGoals;
          if (!weeklyGoals && docData.weeklyGoal) {
            // Convert old single goal format to new array format
            weeklyGoals = [{
              id: 'legacy-goal',
              goal: docData.weeklyGoal,
              achieved: docData.goalMet || false
            }];
          } else if (!weeklyGoals) {
            // If no goals at all, initialize empty array
            weeklyGoals = [];
          }
          
          setFormData({
            telephonyStats: docData.telephonyStats || {
              monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
            },
            ticketStats: docData.ticketStats || {
              monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
            },
            weeklyGoals: weeklyGoals,
            // goalMet is kept for backward compatibility
            goalMet: docData.goalMet || false
          });
          // Exit edit mode when navigating to a week with existing data
          setEditMode(false);
        } else {
          setWeekData(null);
          setFormData({
            telephonyStats: {
              monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
            },
            ticketStats: {
              monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
            },
            weeklyGoals: [],
            goalMet: false
          });
          // Automatically enter edit mode when navigating to a week with no data
          if (canEdit) {
            setEditMode(true);
          }
        }
      } catch (err) {
        console.error('Error fetching week data:', err);
        // Only show critical errors to the user
        if (err.code === 'permission-denied') {
          setError('Permission denied: You do not have access to view this data');
        } else {
          console.log('Note: No data for this week found. This is normal for new weeks.');
          setWeekData(null);
          // Initialize empty form data for the new week
          setFormData({
            telephonyStats: {
              monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
              friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
            },
            ticketStats: {
              monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
              friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
            },
            weeklyGoals: [],
            goalMet: false
          });
          // Enter edit mode for new weeks if user has permission
          if (canEdit) {
            setEditMode(true);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchWeekData();
  }, [selectedWeek, canEdit]);
  
  // Fetch previous week data for comparison
  useEffect(() => {
    const fetchPreviousWeekData = async () => {
      try {
        const prevWeek = new Date(weekDates.startDate);
        prevWeek.setDate(prevWeek.getDate() - 7);
        const prevWeekInfo = getWeekInfo(prevWeek);
        
        const prevWeekQuery = query(
          collection(db, 'weekly_statistics'),
          where('year', '==', prevWeekInfo.year),
          where('week', '==', prevWeekInfo.week)
        );
        
        const querySnapshot = await getDocs(prevWeekQuery);
        
        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          setPreviousWeekData(docData);
        } else {
          setPreviousWeekData(null);
        }
      } catch (err) {
        console.error('Error fetching previous week data:', err);
        setPreviousWeekData(null);
      }
    };
    
    if (weekDates && !loading) {
      fetchPreviousWeekData();
    }
  }, [weekDates, loading]);
  
  // Fetch historical data for charts
  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        // Try the complex query first
        try {
          const historyQuery = query(
            collection(db, 'weekly_statistics'),
            orderBy('year'),
            orderBy('week')
            // Removed limit to show all historical data
          );
          
          const querySnapshot = await getDocs(historyQuery);
          const history = [];
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            
            // Calculate telephony totals with proper adjustments
            const telephonyTotals = Object.values(data.telephonyStats || {}).reduce(
              (acc, day) => {
                acc.receivedCalls += Number(day.receivedCalls || 0);
                acc.answeredCalls += Number(day.answeredCalls || 0);
                acc.subtractNumber += Number(day.subtractNumber || 0);
                return acc;
              },
              { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
            );
            
            // Calculate adjusted values
            const adjustedReceivedCalls = Math.max(0, telephonyTotals.receivedCalls - telephonyTotals.subtractNumber);
            const adjustedAnsweredCalls = Math.max(0, telephonyTotals.answeredCalls - telephonyTotals.subtractNumber);
            
            // Calculate call answer percentage
            const callPercentage = adjustedReceivedCalls > 0 
              ? Math.round((adjustedAnsweredCalls / adjustedReceivedCalls) * 100) 
              : 0;

            // Calculate ticket totals
            const ticketTotals = Object.values(data.ticketStats || {}).reduce(
              (acc, day) => {
                acc.incReceived += Number(day.incReceived || 0);
                acc.incResolved += Number(day.incResolved || 0);
                acc.srReceived += Number(day.srReceived || 0);
                acc.srResolved += Number(day.srResolved || 0);
                return acc;
              },
              { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 }
            );
            
            // Calculate total received, resolved and backlog change
            const totalReceived = ticketTotals.incReceived + ticketTotals.srReceived;
            const totalResolved = ticketTotals.incResolved + ticketTotals.srResolved;
            const backlogChange = totalReceived - totalResolved;
            
            history.push({
              week: `W${data.week}`,
              year: data.year,
              ...ticketTotals,
              receivedCalls: telephonyTotals.receivedCalls,
              answeredCalls: telephonyTotals.answeredCalls,
              subtractNumber: telephonyTotals.subtractNumber,
              adjustedReceivedCalls,
              adjustedAnsweredCalls,
              callPercentage,
              totalReceived,
              totalResolved,
              backlogChange,
              weeklyGoals: data.weeklyGoals || [],
              goalMet: data.goalMet || false,
              goalsAchieved: data.weeklyGoals ? data.weeklyGoals.filter(g => g.achieved).length : 0,
              totalGoals: data.weeklyGoals ? data.weeklyGoals.length : 0
            });
          });
          
          // Sort by year and week
          history.sort((a, b) => {
            if (a.year !== b.year) {
              return a.year - b.year;
            }
            return parseInt(a.week.substring(1)) - parseInt(b.week.substring(1));
          });
          
          setHistoricalData(history); // Show all historical data
        } catch (indexErr) {
          // If we get an index error, fall back to a simpler query
          console.log('Index error in historical data, falling back to simpler query:', indexErr);
          
          const simpleQuery = query(
            collection(db, 'weekly_statistics')
          );
          
          const querySnapshot = await getDocs(simpleQuery);
          const history = [];
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            
            // Calculate ticket totals for each week
            const ticketTotals = Object.values(data.ticketStats || {}).reduce(
              (acc, day) => {
                acc.incReceived += Number(day.incReceived || 0);
                acc.incResolved += Number(day.incResolved || 0);
                acc.srReceived += Number(day.srReceived || 0);
                acc.srResolved += Number(day.srResolved || 0);
                return acc;
              },
              { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 }
            );
            
            // Calculate telephony totals
            const telephonyTotals = Object.values(data.telephonyStats || {}).reduce(
              (acc, day) => {
                acc.receivedCalls += Number(day.receivedCalls || 0);
                acc.answeredCalls += Number(day.answeredCalls || 0);
                acc.subtractNumber += Number(day.subtractNumber || 0);
                return acc;
              },
              { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
            );
            
            // Adjust received calls by subtracting the subtractNumber
            const adjustedReceivedCalls = telephonyTotals.receivedCalls - telephonyTotals.subtractNumber;
            
            // Calculate call answer percentage
            const callPercentage = adjustedReceivedCalls > 0 
              ? Math.round((telephonyTotals.answeredCalls / adjustedReceivedCalls) * 100) 
              : 0;
            
            // Calculate total received, resolved and backlog change
            const totalReceived = ticketTotals.incReceived + ticketTotals.srReceived;
            const totalResolved = ticketTotals.incResolved + ticketTotals.srResolved;
            const backlogChange = totalReceived - totalResolved;
            
            history.push({
              week: `W${data.week}`,
              year: data.year,
              ...ticketTotals,
              ...telephonyTotals,
              adjustedReceivedCalls,
              callPercentage,
              totalReceived,
              totalResolved,
              backlogChange,
              weeklyGoals: data.weeklyGoals || [],
              goalMet: data.goalMet || false,
              goalsAchieved: data.weeklyGoals ? data.weeklyGoals.filter(g => g.achieved).length : 0,
              totalGoals: data.weeklyGoals ? data.weeklyGoals.length : 0
            });
          });
          
          // Sort by year and week
          history.sort((a, b) => {
            if (a.year !== b.year) {
              return a.year - b.year;
            }
            return parseInt(a.week.substring(1)) - parseInt(b.week.substring(1));
          });
          
          setHistoricalData(history); // Show all historical data
        }
      } catch (err) {
        console.error('Error fetching historical data:', err);
      }
    };
    
    fetchHistoricalData();
  }, [weekData]);
  
  // Add this helper function to determine if a week is in the database
  const isWeekInDatabase = (year, week) => {
    return availableWeeks.some(w => w.year === year && w.week === week);
  };

  const handlePreviousWeek = useCallback(() => {
    const prevWeek = new Date(weekDates.startDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    const weekInfo = getWeekInfo(prevWeek);
    
    setSelectedWeek({
      year: weekInfo.year,
      week: weekInfo.week
    });
  }, [weekDates]);
  
  const handleNextWeek = useCallback(() => {
    const nextWeek = new Date(weekDates.startDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const weekInfo = getWeekInfo(nextWeek);
    
    setSelectedWeek({
      year: weekInfo.year,
      week: weekInfo.week
    });
  }, [weekDates]);
  
  const handleTelephonyChange = (day, field, value) => {
    setFormData({
      ...formData,
      telephonyStats: {
        ...formData.telephonyStats,
        [day]: {
          ...formData.telephonyStats[day],
          [field]: Number(value)
        }
      }
    });
  };
  
  const handleTicketChange = (day, field, value) => {
    setFormData({
      ...formData,
      ticketStats: {
        ...formData.ticketStats,
        [day]: {
          ...formData.ticketStats[day],
          [field]: Number(value)
        }
      }
    });
  };
  
  const handleSave = async () => {
    try {
      // Get the email from either currentUser, user or use a default for development
      const userEmail = currentUser?.email || user?.email || 'dev@example.com';

      const dataToSave = {
        year: selectedWeek.year,
        week: selectedWeek.week,
        telephonyStats: formData.telephonyStats,
        ticketStats: formData.ticketStats,
        weeklyGoals: formData.weeklyGoals,
        // Keep goalMet field for backward compatibility
        goalMet: formData.weeklyGoals.some(goal => goal.achieved),
        lastUpdatedBy: userEmail,
        lastUpdatedAt: new Date(),
      };

      if (weekData?.id) {
        // Update existing record
        await updateDoc(doc(db, 'weekly_statistics', weekData.id), dataToSave);
      } else {
        // Create new record
        await addDoc(collection(db, 'weekly_statistics'), {
          ...dataToSave,
          createdBy: userEmail,
          createdAt: new Date(),
        });
      }
      
      // Update weekData immediately with saved data to avoid triggering fetchWeekData
      setWeekData({
        id: weekData?.id || 'temp-id',
        ...dataToSave
      });
      
      // Exit edit mode immediately after successful save
      setEditMode(false);
      
      // Inline success feedback (chip with exit animation)
      setSaveStatus('success');
      setSaveStatusMessage('');
      setSaveStatusOpen(true);
      setTimeout(() => setSaveStatusOpen(false), 2500);
      
      // Refresh data in background (no loading state)
      const refreshData = async () => {
        const weekQuery = query(
          collection(db, 'weekly_statistics'),
          where('year', '==', selectedWeek.year),
          where('week', '==', selectedWeek.week)
        );
        
        const querySnapshot = await getDocs(weekQuery);
        
        if (!querySnapshot.empty) {
          setWeekData({
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          });
        }
        
        // Refresh available weeks after saving
        await fetchAvailableWeeks();
      };
      
      // Run refresh in background without blocking UI
      refreshData().catch(console.error);
      
    } catch (err) {
      console.error('Error saving week data:', err);
      setError('Failed to save data. Please try again.');
      setSaveStatus('error');
      setSaveStatusMessage('Failed to save data. Please try again.');
      setSaveStatusOpen(true);
      setTimeout(() => setSaveStatusOpen(false), 4000);
    }
  };
  
  const handleCancel = useCallback(() => {
    // Reset form data to original weekData
    if (weekData) {
      setFormData({
        telephonyStats: weekData.telephonyStats || {
          monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
        },
        ticketStats: weekData.ticketStats || {
          monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
        },
        weeklyGoals: weekData.weeklyGoals || [],
        goalMet: weekData.goalMet || false
      });
    } else {
      // If no weekData, reset to empty
      setFormData({
        telephonyStats: {
          monday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          tuesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          wednesday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          thursday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
          friday: { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 },
        },
        ticketStats: {
          monday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          tuesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          wednesday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          thursday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
          friday: { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 },
        },
        weeklyGoals: [],
        goalMet: false
      });
    }
    setEditMode(false);
  }, [weekData]);
  
  // Calculate telephony stats percentages
  const calculateCallPercentage = useCallback((day) => {
    // Helper function to calculate totals
    const calculateTotals = () => {
      return Object.keys(formData.telephonyStats).reduce(
        (acc, day) => {
          const dayData = formData.telephonyStats[day];
          acc.receivedCalls += Number(dayData.receivedCalls || 0);
          acc.answeredCalls += Number(dayData.answeredCalls || 0);
          acc.subtractNumber += Number(dayData.subtractNumber || 0);
          return acc;
        },
        { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
      );
    };

    if (!day) {
      // Calculate for the whole week
      const totals = calculateTotals();
      const adjustedReceived = Math.max(0, Number(totals.receivedCalls) - Number(totals.subtractNumber));
      const adjustedAnswered = Math.max(0, Number(totals.answeredCalls) - Number(totals.subtractNumber));
      
      if (adjustedReceived <= 0) return 0;
      return ((adjustedAnswered / adjustedReceived) * 100).toFixed(1);
    } else {
      // Calculate for a specific day
      const { receivedCalls, answeredCalls, subtractNumber } = formData.telephonyStats[day];
      const adjustedReceived = Math.max(0, Number(receivedCalls) - Number(subtractNumber));
      const adjustedAnswered = Math.max(0, Number(answeredCalls) - Number(subtractNumber));
      
      if (adjustedReceived <= 0) return 0;
      return ((adjustedAnswered / adjustedReceived) * 100).toFixed(1);
    }
  }, [formData.telephonyStats]);

  // Calculate telephony totals
  const calculateTelephonyTotals = () => {
    return Object.keys(formData.telephonyStats).reduce(
      (acc, day) => {
        const dayData = formData.telephonyStats[day];
        acc.receivedCalls += Number(dayData.receivedCalls || 0);
        acc.answeredCalls += Number(dayData.answeredCalls || 0);
        acc.subtractNumber += Number(dayData.subtractNumber || 0);
        return acc;
      },
      { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
    );
  };
  
  // Calculate ticket totals
  const calculateTicketTotals = () => {
    return Object.keys(formData.ticketStats).reduce(
      (acc, day) => {
        const dayData = formData.ticketStats[day];
        acc.incReceived += Number(dayData.incReceived || 0);
        acc.incResolved += Number(dayData.incResolved || 0);
        acc.srReceived += Number(dayData.srReceived || 0);
        acc.srResolved += Number(dayData.srResolved || 0);
        return acc;
      },
      { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 }
    );
  };
  
  // Add a function to calculate daily ticket totals for a specific day
  const calculateDailyTicketTotals = (day) => {
    const dayData = formData.ticketStats[day];
    const received = Number(dayData.incReceived || 0) + Number(dayData.srReceived || 0);
    const resolved = Number(dayData.incResolved || 0) + Number(dayData.srResolved || 0);
    const difference = received - resolved;
    
    return { received, resolved, difference };
  };
  
  const ticketTotals = calculateTicketTotals();
  const totalReceived = ticketTotals.incReceived + ticketTotals.srReceived;
  const totalResolved = ticketTotals.incResolved + ticketTotals.srResolved;
  const backlogChange = totalReceived - totalResolved;

  // Determine which days have data entered
  const getDaysWithData = useCallback(() => {
    const daysWithData = [];
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    
    dayOrder.forEach(day => {
      const telephony = formData.telephonyStats[day];
      const tickets = formData.ticketStats[day];
      
      // Check if day has any telephony data
      const hasTelephonyData = (telephony.receivedCalls > 0 || telephony.answeredCalls > 0);
      
      // Check if day has any ticket data
      const hasTicketData = (tickets.incReceived > 0 || tickets.incResolved > 0 || 
                            tickets.srReceived > 0 || tickets.srResolved > 0);
      
      if (hasTelephonyData || hasTicketData) {
        daysWithData.push(day);
      }
    });
    
    return daysWithData;
  }, [formData]);

  // Calculate current week totals only for days that have data
  const getCurrentWeekTotalsForComparison = useCallback(() => {
    const daysWithData = getDaysWithData();
    
    if (daysWithData.length === 0) {
      return {
        telephony: { adjustedReceivedCalls: 0, adjustedAnsweredCalls: 0, answerRate: 0 },
        tickets: { totalReceived: 0, totalResolved: 0, backlogChange: 0 }
      };
    }
    
    // Calculate telephony totals only for days with data
    const telephonyTotals = daysWithData.reduce(
      (acc, day) => {
        const dayData = formData.telephonyStats[day];
        acc.receivedCalls += Number(dayData.receivedCalls || 0);
        acc.answeredCalls += Number(dayData.answeredCalls || 0);
        acc.subtractNumber += Number(dayData.subtractNumber || 0);
        return acc;
      },
      { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
    );
    
    // Calculate ticket totals only for days with data
    const ticketTotals = daysWithData.reduce(
      (acc, day) => {
        const dayData = formData.ticketStats[day];
        acc.incReceived += Number(dayData.incReceived || 0);
        acc.incResolved += Number(dayData.incResolved || 0);
        acc.srReceived += Number(dayData.srReceived || 0);
        acc.srResolved += Number(dayData.srResolved || 0);
        return acc;
      },
      { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 }
    );
    
    const adjustedReceived = Math.max(0, telephonyTotals.receivedCalls - telephonyTotals.subtractNumber);
    const adjustedAnswered = Math.max(0, telephonyTotals.answeredCalls - telephonyTotals.subtractNumber);
    const answerRate = adjustedReceived > 0 
      ? ((adjustedAnswered / adjustedReceived) * 100).toFixed(1) 
      : 0;
    
    return {
      telephony: {
        adjustedReceivedCalls: adjustedReceived,
        adjustedAnsweredCalls: adjustedAnswered,
        answerRate: Number(answerRate)
      },
      tickets: {
        totalReceived: ticketTotals.incReceived + ticketTotals.srReceived,
        totalResolved: ticketTotals.incResolved + ticketTotals.srResolved,
        backlogChange: (ticketTotals.incReceived + ticketTotals.srReceived) - (ticketTotals.incResolved + ticketTotals.srResolved)
      }
    };
  }, [formData, getDaysWithData]);

  // Get previous week totals for comparison - only for days that have data this week
  const getPreviousWeekTotals = useCallback(() => {
    if (!previousWeekData) return null;
    
    const daysWithData = getDaysWithData();
    
    // If no days have data yet, return null
    if (daysWithData.length === 0) return null;
    
    // Calculate totals only for days that have data this week
    const prevTelephonyTotals = daysWithData.reduce(
      (acc, day) => {
        const dayData = previousWeekData.telephonyStats?.[day];
        if (dayData) {
          acc.receivedCalls += Number(dayData.receivedCalls || 0);
          acc.answeredCalls += Number(dayData.answeredCalls || 0);
          acc.subtractNumber += Number(dayData.subtractNumber || 0);
        }
        return acc;
      },
      { receivedCalls: 0, answeredCalls: 0, subtractNumber: 0 }
    );
    
    const prevTicketTotals = daysWithData.reduce(
      (acc, day) => {
        const dayData = previousWeekData.ticketStats?.[day];
        if (dayData) {
          acc.incReceived += Number(dayData.incReceived || 0);
          acc.incResolved += Number(dayData.incResolved || 0);
          acc.srReceived += Number(dayData.srReceived || 0);
          acc.srResolved += Number(dayData.srResolved || 0);
        }
        return acc;
      },
      { incReceived: 0, incResolved: 0, srReceived: 0, srResolved: 0 }
    );
    
    const adjustedPrevReceived = Math.max(0, prevTelephonyTotals.receivedCalls - prevTelephonyTotals.subtractNumber);
    const adjustedPrevAnswered = Math.max(0, prevTelephonyTotals.answeredCalls - prevTelephonyTotals.subtractNumber);
    const prevAnswerRate = adjustedPrevReceived > 0 
      ? ((adjustedPrevAnswered / adjustedPrevReceived) * 100).toFixed(1) 
      : 0;
    
    return {
      telephony: {
        ...prevTelephonyTotals,
        adjustedReceivedCalls: adjustedPrevReceived,
        adjustedAnsweredCalls: adjustedPrevAnswered,
        answerRate: Number(prevAnswerRate)
      },
      tickets: {
        ...prevTicketTotals,
        totalReceived: prevTicketTotals.incReceived + prevTicketTotals.srReceived,
        totalResolved: prevTicketTotals.incResolved + prevTicketTotals.srResolved,
        backlogChange: (prevTicketTotals.incReceived + prevTicketTotals.srReceived) - (prevTicketTotals.incResolved + prevTicketTotals.srResolved)
      },
      daysCompared: daysWithData.length,
      daysLabels: daysWithData.map(d => {
        const dayMap = {
          monday: 'Mon',
          tuesday: 'Tue',
          wednesday: 'Wed',
          thursday: 'Thu',
          friday: 'Fri'
        };
        return dayMap[d] || d.charAt(0).toUpperCase() + d.slice(1);
      })
    };
  }, [previousWeekData, getDaysWithData]);

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle if not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'ArrowLeft' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handlePreviousWeek();
      } else if (e.key === 'ArrowRight' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleNextWeek();
      } else if (e.key === 'e' && (e.ctrlKey || e.metaKey) && canEdit && !editMode) {
        e.preventDefault();
        setEditMode(true);
      } else if (e.key === 'Escape' && editMode) {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [canEdit, editMode, handlePreviousWeek, handleNextWeek, handleCancel]);

  // Scroll to top when week changes
  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedWeek, loading]);

  // Calculate comparison with previous week
  const calculateComparison = useCallback((currentValue, previousValue) => {
    if (previousValue === null || previousValue === undefined) {
      return { change: 0, percentage: 0, trend: 'none' };
    }
    const change = currentValue - previousValue;
    if (previousValue === 0) {
      // If previous was 0 and current is not, show percentage increase
      if (currentValue > 0) {
        return { change, percentage: 100, trend: 'up' };
      }
      return { change: 0, percentage: 0, trend: 'none' };
    }
    const percentage = ((change / previousValue) * 100).toFixed(1);
    const trend = change > 0 ? 'up' : change < 0 ? 'down' : 'none';
    return { change, percentage: Math.abs(percentage), trend };
  }, []);

  // Check if metrics meet benchmarks
  const checkBenchmarks = useCallback(() => {
    const answerRate = Number(calculateCallPercentage());
    return {
      answerRate: {
        met: answerRate >= 90,
        value: answerRate,
        target: 90,
        badge: answerRate >= 90
      }
    };
  }, [calculateCallPercentage]);

  const benchmarks = checkBenchmarks();

  // Prepare data for mini charts
  const dailyTelephonyChartData = useMemo(() => {
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    return dayOrder.map(day => {
      const dayData = formData.telephonyStats[day];
      const adjustedReceived = Math.max(0, Number(dayData.receivedCalls) - Number(dayData.subtractNumber));
      const adjustedAnswered = Math.max(0, Number(dayData.answeredCalls) - Number(dayData.subtractNumber));
      const answerRate = adjustedReceived > 0 ? ((adjustedAnswered / adjustedReceived) * 100).toFixed(1) : 0;
      return {
        day: day.charAt(0).toUpperCase() + day.slice(1, 3),
        received: adjustedReceived,
        answered: adjustedAnswered,
        answerRate: Number(answerRate)
      };
    });
  }, [formData.telephonyStats]);

  const dailyTicketChartData = useMemo(() => {
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    return dayOrder.map(day => {
      const dayData = formData.ticketStats[day];
      const received = Number(dayData.incReceived || 0) + Number(dayData.srReceived || 0);
      const resolved = Number(dayData.incResolved || 0) + Number(dayData.srResolved || 0);
      return {
        day: day.charAt(0).toUpperCase() + day.slice(1, 3),
        received,
        resolved,
        difference: received - resolved
      };
    });
  }, [formData.ticketStats]);

  // Function to generate random emojis (3-5 emojis)
  const generateRandomEmojis = useCallback(() => {
    const emojiPool = ['🆘','🛟','🎈', '🎉', '🎊', '✨', '🌟', '⭐', '💫', '🎁', '🏆', '🥳', '🎯', '💯', '🔥', '🚀', '💪', '🎪'];
    const numEmojis = Math.floor(Math.random() * 3) + 3; // Random number between 3 and 5
    const selectedEmojis = [];
    
    for (let i = 0; i < numEmojis; i++) {
      const randomIndex = Math.floor(Math.random() * emojiPool.length);
      selectedEmojis.push(emojiPool[randomIndex]);
    }
    
    return selectedEmojis.join('');
  }, []);

  // Calculate if the weekly answer rate is 90% or above
  useEffect(() => {
    // Get the current weekly answer rate
    const weeklyAnswerRate = Number(calculateCallPercentage());
    
    // Set the celebration flag if the rate is 90% or above
    if (weeklyAnswerRate >= 90) {
      setShowCelebration(true);
      // Generate new random emojis each time celebration is triggered
      setCelebrationEmojis(generateRandomEmojis());
      setHasLaunchedBalloons(false); // Reset to allow new animation
    } else {
      setShowCelebration(false);
      setHasLaunchedBalloons(false); // Reset when celebration is hidden
    }
    
    // Log for debugging
    console.log('Weekly Answer Rate:', weeklyAnswerRate, 'Show Celebration:', weeklyAnswerRate >= 90);
  }, [formData, weekData, calculateCallPercentage, generateRandomEmojis]); // Dependencies to ensure it updates when data changes

  // Launch balloons animation when celebration should show
  useEffect(() => {
    if (showCelebration && viewTab === 0 && !hasLaunchedBalloons) {
      // Small delay to ensure component is mounted and ref is set
      const timer = setTimeout(() => {
        if (balloonsRef.current && balloonsRef.current.launchAnimation) {
          balloonsRef.current.launchAnimation();
          setHasLaunchedBalloons(true);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [showCelebration, viewTab, hasLaunchedBalloons]);

  // Handle CSV file upload
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Ref for balloons animation
  const balloonsRef = useRef(null);
  
  const handleCsvDialogOpen = () => {
    setCsvDialogOpen(true);
  };
  
  const handleCsvDialogClose = () => {
    setCsvDialogOpen(false);
  };
  
  const handleCsvFileSelect = () => {
    fileInputRef.current.click();
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const csvFiles = files.filter(file => file.name.toLowerCase().endsWith('.csv'));

    if (csvFiles.length === 0) {
      setSnackbarMessage('Please drop only CSV files');
      setSnackbarOpen(true);
      return;
    }

    if (csvFiles.length !== files.length) {
      setSnackbarMessage(`Only ${csvFiles.length} of ${files.length} files were CSV files. Processing CSV files only.`);
      setSnackbarOpen(true);
    }

    // Create a synthetic event object to match the expected format
    const syntheticEvent = {
      target: {
        files: csvFiles
      }
    };

    handleCsvUpload(syntheticEvent);
  };
  
  const handleCsvUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Determine which day to update based on current day
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Convert to day name (if today is Tuesday, we want to update Monday)
    const dayToUpdate = getDayToUpdate(dayOfWeek);
    
    if (!dayToUpdate) {
      setError("Cannot import data on weekends. Please import on a weekday.");
      return;
    }
    
    // First, make sure we're in edit mode
    if (!editMode) {
      setEditMode(true);
    }
    
    // Track successful imports
    const importResults = {
      ticketImported: false,
      telephonyImported: false,
      errors: []
    };
    
    // Process each uploaded file using Promise.all to wait for all files
    const filePromises = Array.from(files).map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            console.log(`Processing file: ${file.name}`);
            const csvData = parseCsvData(e.target.result);
            
            if (csvData) {
              // Update the form data with CSV values based on type
              if (csvData.type === 'ticket') {
                console.log('Detected ticket statistics CSV', csvData.data);
                setFormData(prevData => ({
                  ...prevData,
                  ticketStats: {
                    ...prevData.ticketStats,
                    [dayToUpdate]: {
                      ...prevData.ticketStats[dayToUpdate],
                      ...csvData.data
                    }
                  }
                }));
                importResults.ticketImported = true;
              } else if (csvData.type === 'telephony') {
                console.log('Detected telephony statistics CSV', csvData.data);
                setFormData(prevData => ({
                  ...prevData,
                  telephonyStats: {
                    ...prevData.telephonyStats,
                    [dayToUpdate]: {
                      ...prevData.telephonyStats[dayToUpdate],
                      ...csvData.data
                    }
                  }
                }));
                importResults.telephonyImported = true;
              } else {
                console.warn(`Could not determine type for file: ${file.name}`);
                importResults.errors.push(`Could not process "${file.name}" - unknown format`);
              }
            } else {
              console.warn(`Failed to parse CSV data from file: ${file.name}`);
              importResults.errors.push(`Could not parse "${file.name}"`);
            }
          } catch (error) {
            console.error(`Error processing CSV file "${file.name}":`, error);
            importResults.errors.push(`Error in "${file.name}": ${error.message}`);
          }
          resolve();
        };
        
        reader.onerror = (error) => {
          console.error(`Error reading file "${file.name}":`, error);
          importResults.errors.push(`Could not read "${file.name}"`);
          reject(error);
        };
        
        reader.readAsText(file);
      });
    });
    
    try {
      // Wait for all files to be processed
      await Promise.all(filePromises);
      
      // Small delay to ensure state updates have propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Show appropriate success/error messages
      if (importResults.ticketImported && importResults.telephonyImported) {
        setSnackbarMessage(`Successfully imported both ticket and telephony data for ${dayToUpdate.charAt(0).toUpperCase() + dayToUpdate.slice(1)}`);
        setSnackbarOpen(true);
      } else if (importResults.ticketImported) {
        setSnackbarMessage(`Imported ticket data for ${dayToUpdate.charAt(0).toUpperCase() + dayToUpdate.slice(1)}`);
        setSnackbarOpen(true);
      } else if (importResults.telephonyImported) {
        setSnackbarMessage(`Imported telephony data for ${dayToUpdate.charAt(0).toUpperCase() + dayToUpdate.slice(1)}`);
        setSnackbarOpen(true);
      } else if (importResults.errors.length > 0) {
        setError(importResults.errors.join('; '));
      } else {
        setError('No data could be imported from the selected file(s).');
      }
      
      // Close dialog after a small delay to allow focus to return properly
      setTimeout(() => {
        handleCsvDialogClose();
      }, 150);
      
    } catch (error) {
      console.error("Error during file processing:", error);
      setError('Failed to process one or more files. Please try again.');
    }
    
    // Clear the file input
    event.target.value = null;
  };
  
  // Get the day to update based on current day
  const getDayToUpdate = (dayOfWeek) => {
    // If today is Sunday (0) or Saturday (6), we don't update
    if (dayOfWeek === 0 || dayOfWeek === 6) return null;
    
    // Map current day to the previous weekday
    const dayMap = {
      1: 'friday', // Monday updates Friday (from previous week)
      2: 'monday', // Tuesday updates Monday
      3: 'tuesday', // Wednesday updates Tuesday
      4: 'wednesday', // Thursday updates Wednesday
      5: 'thursday', // Friday updates Thursday
    };
    
    return dayMap[dayOfWeek];
  };
  
  // Parse CSV data
  const parseCsvData = (csvText) => {
    try {
      const lines = csvText.split(/\r?\n/);
      
      console.log('=== CSV Parsing Debug ===');
      console.log('Total lines:', lines.length);
      console.log('First 5 lines:', lines.slice(0, 5));
      
      // First, try to detect if this is a telephony statistics CSV
      const isTelephony = isTelephonyStatsCSV(lines);
      console.log('Is telephony CSV:', isTelephony);
      
      if (isTelephony) {
        return parseTelephonyStatsCSV(lines);
      }
      
      // If not telephony stats, assume it's ticket statistics
      console.log('Attempting to parse as ticket statistics');
      let incidentData = null;
      let serviceRequestData = null;
      
      // Skip header row and process each line
      for (let i = 1; i < lines.length; i++) {
        // Try different delimiters (comma, semicolon, tab)
        let cells = lines[i].split(',');
        if (cells.length < 3) cells = lines[i].split(';');
        if (cells.length < 3) cells = lines[i].split('\t');
        
        // Skip empty lines
        if (cells.length < 3 || !cells[0].trim()) continue;
        
        const type = cells[0].trim();
        // Try to find numeric values in columns
        const created = parseInt(cells[1].trim().replace(/\D/g, ''), 10) || 0;
        const resolved = parseInt(cells[2].trim().replace(/\D/g, ''), 10) || 0;
        
        console.log(`Parsed line: Type=${type}, Created=${created}, Resolved=${resolved}`);
        
        if (type.toLowerCase().includes('incident')) {
          incidentData = { created, resolved };
        } else if (type.toLowerCase().includes('service')) {
          serviceRequestData = { created, resolved };
        }
      }
      
      if (incidentData && serviceRequestData) {
        console.log('Successfully parsed ticket statistics CSV data');
        
        return {
          type: 'ticket',
          data: {
            incReceived: incidentData.created,
            incResolved: incidentData.resolved,
            srReceived: serviceRequestData.created,
            srResolved: serviceRequestData.resolved
          }
        };
      } else {
        console.error('Failed to extract required ticket data from CSV');
        return null;
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      return null;
    }
  };
  
  // Check if CSV is telephony statistics format
  const isTelephonyStatsCSV = (lines) => {
    for (let i = 0; i < Math.min(lines.length, 15); i++) {
      const line = lines[i].toLowerCase();

      // Simple format: "Received Calls","Answered Calls"
      const simpleCells = line.split(',').map(c => c.trim().replace(/"/g, ''));
      if (simpleCells.some(c => c === 'received calls') && simpleCells.some(c => c === 'answered calls')) {
        return true;
      }
      
      // Look for indicators of the new format (tab-delimited with specific headers)
      const cells = line.split('\t');
      if (cells.length >= 5) {
        // Look for key column headers that would indicate the new format
        const hasQueueColumn = cells.some(cell => cell.trim() === 'queue');
        const hasExtensionColumn = cells.some(cell => cell.trim() === 'extension');
        const hasReceivedCalls = cells.some(cell => cell.trim().includes('queue received calls'));
        const hasServicedCalls = cells.some(cell => cell.trim().includes('queue serviced calls'));
        
        if (hasQueueColumn && hasExtensionColumn && (hasReceivedCalls || hasServicedCalls)) {
          return true;
        }
      }
      
      // Old format detection
      if (line.includes('queue performance overview') || 
          line.includes('sos support') ||
          (line.includes('received') && line.includes('serviced'))) {
        return true;
      }
    }
    return false;
  };
  
  // Parse telephony statistics CSV
  const parseTelephonyStatsCSV = (lines) => {
    try {
      // Simple format: "Received Calls","Answered Calls" header followed by a single data row
      for (let i = 0; i < Math.min(lines.length, 5); i++) {
        const headerCells = lines[i].split(',').map(c => c.trim().replace(/"/g, '').toLowerCase());
        const rcIdx = headerCells.indexOf('received calls');
        const acIdx = headerCells.indexOf('answered calls');
        if (rcIdx !== -1 && acIdx !== -1 && i + 1 < lines.length) {
          const dataCells = lines[i + 1].split(',').map(c => c.trim().replace(/"/g, ''));
          const receivedCalls = parseInt(dataCells[rcIdx]) || 0;
          const answeredCalls = parseInt(dataCells[acIdx]) || 0;
          console.log(`Simple telephony format: Received=${receivedCalls}, Answered=${answeredCalls}`);
          return {
            type: 'telephony',
            data: { receivedCalls, answeredCalls, subtractNumber: 0 }
          };
        }
      }

      // For the new format we need to find the header row first to determine column positions
      let headerRowIndex = -1;
      let queueIndex = -1;
      let extensionIndex = -1;
      let queueReceivedIndex = undefined;
      let queueServicedIndex = undefined;
      let extensionServicedIndex = undefined;
      
      console.log('Analyzing CSV file...');
      
      // First pass: find header row and locate column indices
      for (let i = 0; i < Math.min(lines.length, 10); i++) { // Check first 10 lines for headers
        const line = lines[i];
        const cells = line.split('\t'); // Tab-delimited format
        
        // Log the first few rows for debugging
        if (i < 3) {
          console.log(`Row ${i}: ${JSON.stringify(cells)}`);
        }
        
        // Try with different delimiters if tab doesn't work
        let processedCells = cells;
        if (cells.length <= 3) {
          processedCells = line.split(',');
          if (processedCells.length > cells.length) {
            console.log('Using comma delimiter instead of tab');
          }
        }
        
        // Check if this is the header row
        if (processedCells.some(cell => cell && cell.trim().toLowerCase() === 'queue') &&
            processedCells.some(cell => cell && cell.trim().toLowerCase() === 'extension')) {
          
          headerRowIndex = i;
          console.log(`Found header row at line ${i}: ${line}`);
          
          // Find column indices - log each column mapping
          for (let j = 0; j < processedCells.length; j++) {
            if (!processedCells[j]) continue;
            
            const cellLower = processedCells[j].trim().toLowerCase();
            console.log(`Column ${j}: "${cellLower}"`);
            
            if (cellLower === 'queue') {
              queueIndex = j;
              console.log(`Queue column: ${j}`);
            }
            else if (cellLower === 'extension') {
              extensionIndex = j;
              console.log(`Extension column: ${j}`);
            }
            else if (cellLower.includes('queue received')) {
              queueReceivedIndex = j;
              console.log(`Queue Received column: ${j}`);
            }
            else if (cellLower.includes('queue serviced')) {
              queueServicedIndex = j;
              console.log(`Queue Serviced column: ${j}`);
            }
            else if (cellLower.includes('extension serviced')) {
              extensionServicedIndex = j;
              console.log(`Extension Serviced column: ${j}`);
            }
          }
          
          break;
        }
      }
      
      // Debug column indices
      console.log(`Column indices: Queue=${queueIndex}, Extension=${extensionIndex}, QueueReceived=${queueReceivedIndex}, QueueServiced=${queueServicedIndex}, ExtensionServiced=${extensionServicedIndex}`);
      
      // If we couldn't find the header row or essential columns, try the old format
      if (headerRowIndex === -1 || 
          queueIndex === -1 || 
          extensionIndex === -1) {
        console.log('Could not find required columns - trying old format');
        return parseOldTelephonyStatsCSV(lines);
      }
      
      // Variables to store the data we need
      let receivedCalls = 0;
      let answeredCalls = 0;
      let subtractNumber = 0;
      
      // Second pass: process all rows to find the data we need
      for (let i = headerRowIndex + 1; i < lines.length; i++) {
        const line = lines[i];
        let cells = line.split('\t');
        
        // Try with different delimiters if tab doesn't work
        if (cells.length <= 3) {
          const commaCells = line.split(',');
          if (commaCells.length > cells.length) {
            cells = commaCells;
          }
        }
        
        if (cells.length <= Math.max(queueIndex, extensionIndex, queueReceivedIndex || 0, queueServicedIndex || 0, extensionServicedIndex || 0)) {
          console.log(`Skipping row ${i} - insufficient cells: ${cells.length}`);
          continue;
        }
        
        const queueName = cells[queueIndex]?.trim() || '';
        const extension = cells[extensionIndex]?.trim() || '';
        
        // Debug each row
        console.log(`Row ${i}: Queue="${queueName}", Extension="${extension}"`);
        
        // Check if this is the "Totals" row - this contains the main statistics
        if (extension.toLowerCase() === 'totals' || 
            (queueName.toLowerCase().includes('totals') && extension === '')) {
          console.log('Found Totals row');
          if (queueReceivedIndex !== undefined && queueServicedIndex !== undefined) {
            receivedCalls = parseInt(cells[queueReceivedIndex]) || 0;
            answeredCalls = parseInt(cells[queueServicedIndex]) || 0;
            console.log(`Totals row: Received=${receivedCalls}, Answered=${answeredCalls}`);
          }
        }
        
        // Check if this is the "SOS Support Q Bot" row - this contains the subtract number
        if (extension.toLowerCase().includes('sos support q bot') || 
            extension.toLowerCase().includes('0099 sos support q bot')) {
          console.log('Found SOS Support Q Bot row');
          if (extensionServicedIndex !== undefined) {
            subtractNumber = parseInt(cells[extensionServicedIndex]) || 0;
            console.log(`SOS Support Q Bot row: Subtract=${subtractNumber}`);
          }
        }
      }
      
      // If we found the required data, return it
      if (receivedCalls > 0 && answeredCalls > 0) {
        console.log(`Final parsed data: Received=${receivedCalls}, Answered=${answeredCalls}, Subtract=${subtractNumber}`);
        return {
          type: 'telephony',
          data: {
            receivedCalls,
            answeredCalls,
            subtractNumber
          }
        };
      }
      
      console.error('Failed to extract telephony data from CSV - missing required data');
      return null;
    } catch (error) {
      console.error('Error parsing telephony CSV:', error);
      return null;
    }
  };
  
  // Legacy parser for backward compatibility
  const parseOldTelephonyStatsCSV = (lines) => {
    try {
      let receivedCalls = 0;
      let answeredCalls = 0;
      let subtractNumber = 0;
      
      // Process the lines to find the main queue info and totals info
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const cells = line.split(',');
        
        // Skip lines that don't have enough cells
        if (cells.length < 6) continue;
        
        // Look for the main queue line - can be "8000 - SOS Support" or similar
        if (cells[0].trim() && (
            cells[0].includes('8000') || 
            cells[0].includes('SOS Support') ||
            // Also match the line with the queue name and received/serviced counts
            (cells[4].trim() && cells[5].trim() && 
             !isNaN(parseInt(cells[4].trim())) && 
             !isNaN(parseInt(cells[5].trim())))
           )) {
          // Try to extract the received and answered calls
          // The format might be like "131" and "119"
          const receivedCell = cells[4].trim();
          const answeredCell = cells[5].trim();
          
          if (receivedCell && answeredCell) {
            const receivedMatch = receivedCell.match(/\d+/);
            const answeredMatch = answeredCell.match(/\d+/);
            
            if (receivedMatch) receivedCalls = parseInt(receivedMatch[0]);
            if (answeredMatch) answeredCalls = parseInt(answeredMatch[0]);
            
            console.log(`Found main queue: Received=${receivedCalls}, Answered=${answeredCalls}`);
          }
        }
        
        // Look for the totals line - typically contains "Totals"
        if (cells[1] && cells[1].toLowerCase().includes('totals')) {
          // Try to extract the number of totals calls
          // The format might be like "26" or "26 - Polls"
          const subtractCell = cells[5].trim();
          
          if (subtractCell) {
            const subtractMatch = subtractCell.match(/\d+/);
            if (subtractMatch) subtractNumber = parseInt(subtractMatch[0]);
            console.log(`Found totals queue: Subtract=${subtractNumber}`);
          }
        }
      }
      
      if (receivedCalls > 0) {
        console.log('Successfully parsed telephony statistics CSV data (old format)');
        
        return {
          type: 'telephony',
          data: {
            receivedCalls,
            answeredCalls, 
            subtractNumber
          }
        };
      } else {
        console.error('Failed to extract telephony data from CSV (old format)');
        return null;
      }
    } catch (error) {
      console.error('Error parsing old telephony CSV format:', error);
      return null;
    }
  };
  
  // New handlers for working with multiple goals
  const handleAddGoal = () => {
    setFormData(prevData => ({
      ...prevData,
      weeklyGoals: [
        ...prevData.weeklyGoals,
        { id: Date.now().toString(), goal: '', achieved: false }
      ]
    }));
  };

  const handleUpdateGoal = (goalId, value) => {
    setFormData(prevData => ({
      ...prevData,
      weeklyGoals: prevData.weeklyGoals.map(goal => 
        goal.id === goalId ? { ...goal, goal: value } : goal
      )
    }));
  };

  const handleToggleGoalAchieved = useCallback(async (goalId) => {
    // First update local state
    let updatedGoals = [];
    
    setFormData(prevData => {
      updatedGoals = prevData.weeklyGoals.map(goal => 
        goal.id === goalId ? { ...goal, achieved: !goal.achieved } : goal
      );
      
      return {
        ...prevData,
        weeklyGoals: updatedGoals
      };
    });
    
    // If we're not in edit mode and we have existing week data, save the change immediately
    if (!editMode && weekData?.id) {
      try {
        const userEmail = currentUser?.email || user?.email || 'dev@example.com';
        
        await updateDoc(doc(db, 'weekly_statistics', weekData.id), {
          weeklyGoals: updatedGoals,
          lastUpdatedBy: userEmail,
          lastUpdatedAt: new Date(),
        });
        
        // Show temporary success message
        const tempMessage = document.createElement('div');
        tempMessage.style.position = 'fixed';
        tempMessage.style.bottom = '20px';
        tempMessage.style.right = '20px';
        tempMessage.style.backgroundColor = '#4caf50';
        tempMessage.style.color = 'white';
        tempMessage.style.padding = '10px 20px';
        tempMessage.style.borderRadius = '4px';
        tempMessage.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        tempMessage.style.zIndex = '9999';
        tempMessage.textContent = 'Goal status updated';
        document.body.appendChild(tempMessage);
        
        const messageId = `goal-status-message-${Date.now()}`;
        tempMessage.id = messageId;
        
        setTimeout(() => {
          const messageElement = document.getElementById(messageId);
          if (messageElement && document.body.contains(messageElement)) {
            document.body.removeChild(messageElement);
          }
        }, 3000);
      } catch (error) {
        console.error('Error updating goal status:', error);
        setError('Failed to update goal status. Please try again.');
        setTimeout(() => setError(''), 5000);
      }
    }
  }, [editMode, weekData, currentUser, user]);

  const handleRemoveGoal = (goalId) => {
    setFormData(prevData => ({
      ...prevData,
      weeklyGoals: prevData.weeklyGoals.filter(goal => goal.id !== goalId)
    }));
  };

  // Export filtered historical data to Excel
  const handleExportHistoricalData = () => {
    if (filteredHistoricalData.length === 0) {
      setSnackbarMessage('No data to export');
      setSnackbarOpen(true);
      return;
    }

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Prepare data for the main sheet
    const mainSheetData = [
      // Headers
      [
        'Week',
        'Year',
        'Total Received Calls',
        'Total Answered Calls',
        'Subtract Number',
        'Adjusted Received Calls',
        'Adjusted Answered Calls',
        'Call Answer Rate (%)',
        'INC Received',
        'INC Resolved',
        'SR Received',
        'SR Resolved',
        'Total Tickets Received',
        'Total Tickets Resolved',
        'Backlog Change'
      ],
      // Data rows
      ...filteredHistoricalData.map(data => [
        data.week,
        data.year,
        data.receivedCalls || 0,
        data.answeredCalls || 0,
        data.subtractNumber || 0,
        data.adjustedReceivedCalls || 0,
        data.adjustedAnsweredCalls || 0,
        data.callPercentage || 0,
        data.incReceived || 0,
        data.incResolved || 0,
        data.srReceived || 0,
        data.srResolved || 0,
        data.totalReceived || 0,
        data.totalResolved || 0,
        data.backlogChange || 0
      ])
    ];

    // Create the main worksheet
    const mainWorksheet = XLSX.utils.aoa_to_sheet(mainSheetData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 8 },   // Week
      { wch: 6 },   // Year
      { wch: 15 },  // Total Received Calls
      { wch: 15 },  // Total Answered Calls
      { wch: 12 },  // Subtract Number
      { wch: 15 },  // Adjusted Received Calls
      { wch: 15 },  // Adjusted Answered Calls
      { wch: 12 },  // Call Answer Rate (%)
      { wch: 12 },  // INC Received
      { wch: 12 },  // INC Resolved
      { wch: 12 },  // SR Received
      { wch: 12 },  // SR Resolved
      { wch: 15 },  // Total Tickets Received
      { wch: 15 },  // Total Tickets Resolved
      { wch: 12 }   // Backlog Change
    ];
    mainWorksheet['!cols'] = columnWidths;

    // Add the worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Weekly Statistics');

    // Create a summary sheet with key metrics
    const summaryData = [
      ['Weekly Statistics Summary'],
      [''],
      ['Export Date:', new Date().toLocaleDateString()],
      ['Data Range:', showAllWeeks ? 'All available weeks' : `Last ${weeksToShow} weeks`],
      ['Total Weeks:', filteredHistoricalData.length],
      [''],
      ['Key Metrics:'],
      [''],
      ['Average Call Answer Rate:', filteredHistoricalData.length > 0 ? 
        (filteredHistoricalData.reduce((sum, data) => sum + (data.callPercentage || 0), 0) / filteredHistoricalData.length).toFixed(1) + '%' : 'N/A'],
      ['Total Tickets Received:', filteredHistoricalData.reduce((sum, data) => sum + (data.totalReceived || 0), 0)],
      ['Total Tickets Resolved:', filteredHistoricalData.reduce((sum, data) => sum + (data.totalResolved || 0), 0)],
      ['Net Backlog Change:', filteredHistoricalData.reduce((sum, data) => sum + (data.backlogChange || 0), 0)]
    ];

    const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
    summaryWorksheet['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

    // Generate filename with date range
    const dateRange = showAllWeeks 
      ? 'all-weeks' 
      : `last-${weeksToShow}-weeks`;
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `weekly-statistics-${dateRange}-${timestamp}.xlsx`;

    // Save the file
    XLSX.writeFile(workbook, filename);
    
    setSnackbarMessage(`Exported ${filteredHistoricalData.length} weeks of data to Excel`);
    setSnackbarOpen(true);
  };

  return (
    <>
      {/* Balloons Animation - Only show in weekly data view */}
      {showCelebration && viewTab === 0 && celebrationEmojis && (
        <Balloons 
          ref={balloonsRef}
          type="text"
          text={celebrationEmojis}
          fontSize={180}
          color="#000000"
        />
      )}
      
            <Box sx={{ 
        py: 3,
        px: 3,
        height: '100%',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 85px)',
        ...modernScrollbarStyles,
        position: 'relative'
      }}>
        {/* Modern header with title and actions */}
        <Box sx={{ 
          mb: 4, 
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2
        }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 2
            }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  fontWeight: 400,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                Weekly Statistics
                {loading && (
                  <CircularProgress 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 2
            }}>
              {saveStatus && (
                <Grow
                  in={saveStatusOpen}
                  timeout={{ enter: 280, exit: 220 }}
                  onExited={() => {
                    setSaveStatus(null);
                    setSaveStatusMessage('');
                  }}
                  style={{ transformOrigin: 'left center' }}
                >
                  <Chip
                    size="small"
                    icon={saveStatus === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={saveStatus === 'success' ? 'Saved' : saveStatusMessage}
                    color={saveStatus === 'success' ? 'success' : 'error'}
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      boxShadow: (theme) =>
                        saveStatus === 'success'
                          ? `0 2px 12px ${alpha(theme.palette.success.main, 0.35)}`
                          : `0 2px 12px ${alpha(theme.palette.error.main, 0.35)}`,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                        fontSize: '1rem',
                      },
                    }}
                  />
                </Grow>
              )}
              {/* Modern action buttons */}
              {canEdit && !editMode && !loading && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSaveStatusOpen(false);
                    setEditMode(true);
                  }}
                  size="small"
                  startIcon={<EditIcon />}
                  sx={(theme) => ({
                    borderRadius: 2,
                    minWidth: 120,
                    height: 48,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    border: '1.5px solid',
                    borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.dark',
                    },
                  })}
                >
                  Edit
                </Button>
              )}
              {canEdit && editMode && (
                <ButtonGroup
                  variant="outlined"
                  size="small"
                  sx={(theme) => ({
                    borderRadius: 2,
                    minWidth: 320,
                    height: 48,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    overflow: 'hidden',
                    border: '1.5px solid',
                    borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.1),
                    '& .MuiButtonGroup-grouped': {
                      flex: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      height: '100%',
                      border: 'none !important',
                      borderRadius: 0,
                      backgroundColor: 'transparent !important',
                      color: 'primary.main !important',
                      minWidth: 'auto',
                      padding: '6px 16px',
                      '&:hover': {
                        backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                        color: 'primary.dark !important',
                        transform: 'none',
                        boxShadow: 'none',
                      },
                      '&:not(:last-of-type)': {
                        borderRight: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderTop: 'none',
                        borderBottom: 'none',
                        borderLeft: 'none',
                      },
                      '&.MuiButton-root': {
                        border: 'none !important',
                        '&:hover': {
                          backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                          color: 'primary.dark !important',
                        }
                      }
                    },
                  })}
                >
                  <Button onClick={handleSave}>Save</Button>
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button onClick={handleCsvDialogOpen}>Import</Button>
                </ButtonGroup>
              )}

              {/* Modern week selector */}
              <Paper 
                elevation={0}
                sx={(theme) => ({ 
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 2,
                  minWidth: 320,
                  backgroundColor: isWeekInDatabase(selectedWeek.year, selectedWeek.week) 
                    ? 'background.paper' 
                    : alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: isWeekInDatabase(selectedWeek.year, selectedWeek.week)
                    ? 'divider'
                    : alpha(theme.palette.primary.main, 0.2),
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                })}
              >
                {/* Previous Week Button */}
                <MuiTooltip title="Previous Week">
                  <IconButton
                    onClick={handlePreviousWeek}
                    disabled={loading}
                    size="small"
                    sx={(theme) => ({
                      borderRadius: 0,
                      width: 44,
                      height: 44,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: 'text.disabled',
                      }
                    })}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </MuiTooltip>

                {/* Week Display */}
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1
                }}>
                  <CalendarTodayIcon 
                    sx={{ 
                      color: isWeekInDatabase(selectedWeek.year, selectedWeek.week) 
                        ? 'text.secondary' 
                        : 'primary.main',
                      fontSize: '1.2rem'
                    }} 
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: isWeekInDatabase(selectedWeek.year, selectedWeek.week) 
                        ? 'text.primary' 
                        : 'primary.main',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={handleOpenWeekMenu}
                    aria-haspopup="true"
                    aria-expanded={isWeekMenuOpen ? 'true' : undefined}
                    aria-controls={isWeekMenuOpen ? 'week-menu' : undefined}
                  >
                    Week {selectedWeek.week}, {selectedWeek.year}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleOpenWeekMenu}
                    aria-label="Choose week"
                    sx={(theme) => ({
                      ml: -0.5,
                      color: isWeekInDatabase(selectedWeek.year, selectedWeek.week) 
                        ? 'text.secondary' 
                        : 'primary.main',
                      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                    })}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                  
                  {/* Subtle new indicator */}
                  {!isWeekInDatabase(selectedWeek.year, selectedWeek.week) && (
                    <MuiTooltip title="No data saved for this week yet">
                      <Box sx={{ width: 8, height: 8, bgcolor: 'primary.main', borderRadius: '50%' }} />
                    </MuiTooltip>
                  )}
                </Box>

                {/* Next Week Button */}
                <MuiTooltip title="Next Week">
                  <IconButton
                    onClick={handleNextWeek}
                    disabled={loading}
                    size="small"
                    sx={(theme) => ({
                      borderRadius: 0,
                      width: 44,
                      height: 44,
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '&.Mui-disabled': {
                        opacity: 0.5,
                        color: 'text.disabled',
                      }
                    })}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </MuiTooltip>
              </Paper>
              
              {/* Week selection menu */}
              <Menu
                id="week-menu"
                anchorEl={weekMenuAnchorEl}
                open={isWeekMenuOpen}
                onClose={handleCloseWeekMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                MenuListProps={{ dense: true }}
              >
                <MenuItem onClick={() => { handleGoToCurrentWeek(); handleCloseWeekMenu(); }}>
                  Go to current week
                </MenuItem>
                <Divider />
                {availableWeeks.length === 0 && (
                  <MenuItem disabled>No saved weeks</MenuItem>
                )}
                {availableWeeks.map((w) => (
                  <MenuItem 
                    key={`${w.year}-W${w.week}`}
                    selected={w.year === selectedWeek.year && w.week === selectedWeek.week}
                    onClick={() => handleSelectWeek(w.year, w.week)}
                  >
                    Week {w.week}, {w.year}
                  </MenuItem>
                ))}
              </Menu>
              
            </Box>
          </Box>
          
          {/* Error message if any */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ py: 4 }}>
              {/* KPI Skeletons */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton 
                      variant="rectangular" 
                      height={150} 
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                ))}
              </Grid>
              
              {/* Chart Skeletons */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
                </Grid>
              </Grid>
              
              {/* Table Skeletons */}
              <Box sx={{ mb: 4 }}>
                <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
              </Box>
            </Box>
          ) : (
            <>
              <Tabs 
                value={viewTab} 
                onChange={(_, newValue) => setViewTab(newValue)}
                sx={{ 
                  mb: 3, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px 8px 0 0',
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      backgroundColor: alpha('#000', 0.02),
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '2px 2px 0 0',
                  }
                }}
              >
                <Tab label="Weekly Data" />
                <Tab label="Historical View" icon={<BarChartIcon />} iconPosition="start" />
              </Tabs>
              
              {viewTab === 0 ? (
                <>
                  {/* Modern Team Goals Section */}
                  <Paper 
                    sx={{ 
                      mb: 4, 
                      borderRadius: '20px',
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.2s ease-in-out',
                      bgcolor: 'background.paper',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    {/* Header Section */}
                    <Box sx={{
                      p: 2,
                      bgcolor: theme => formData.weeklyGoals.length === 0 && !editMode 
                        ? 'background.default'
                        : alpha(theme.palette.primary.main, 0.05),
                      borderBottom: formData.weeklyGoals.length > 0 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      position: 'relative',
                      '&::after': formData.weeklyGoals.length > 0 ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: theme => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      } : {}
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1
                        }}>
                          <Box sx={{
                            p: 0.75,
                            borderRadius: 1.5,
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FlagIcon 
                              color="primary"
                              sx={{ fontSize: '1.25rem' }}
                            />
                          </Box>
                          <Box>
                            <Typography 
                              variant="body1"
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '1rem'
                              }}
                            >
                              Team Goals
                            </Typography>
                            {formData.weeklyGoals.length > 0 && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                              >
                                {formData.weeklyGoals.filter(g => g.achieved).length} of {formData.weeklyGoals.length} completed
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Content Section */}
                    {formData.weeklyGoals.length === 0 && !editMode ? (
                      <Box sx={{ 
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 1.5
                      }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <FlagIcon 
                            color="primary"
                            sx={{ fontSize: '2rem', opacity: 0.6 }}
                          />
                        </Box>
                        <Typography 
                          variant="body1"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          No goals set for this week
                        </Typography>
                        <Typography 
                          variant="body2"
                          color="text.secondary"
                          sx={{ maxWidth: 350, opacity: 0.7, fontSize: '0.875rem' }}
                        >
                          Set team goals to track progress and align your team's efforts
                        </Typography>
                        {canEdit && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setEditMode(true)}
                            size="small"
                            sx={{ 
                              mt: 0.5,
                              borderRadius: 2,
                              px: 2,
                              py: 0.75
                            }}
                          >
                            Add Goals
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        {editMode ? (
                          // Edit Mode - Multiple Goals UI
                          <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column', 
                            gap: 2,
                          }}>
                            {/* Progress Summary Card */}
                            {formData.weeklyGoals.length > 0 && (
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: theme => alpha(theme.palette.primary.main, 0.1),
                                }}
                              >
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1
                                }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {formData.weeklyGoals.length} goal{formData.weeklyGoals.length !== 1 ? 's' : ''}
                                  </Typography>
                                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                    {formData.weeklyGoals.filter(g => g.achieved).length} completed
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={(formData.weeklyGoals.filter(g => g.achieved).length / formData.weeklyGoals.length) * 100}
                                  sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 4,
                                      bgcolor: 'primary.main',
                                    }
                                  }}
                                />
                              </Paper>
                            )}
                            
                            {/* Goal cards */}
                            {formData.weeklyGoals.length > 0 ? (
                              <Grid container spacing={1.5}>
                                {formData.weeklyGoals.map((goal, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={goal.id}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: theme => alpha(theme.palette.primary.main, 0.1),
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                        borderRadius: 1.5,
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                          boxShadow: 2,
                                          transform: 'translateY(-2px)',
                                        },
                                      }}
                                    >
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        flex: 1,
                                      }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRemoveGoal(goal.id)}
                                          sx={{ 
                                            mt: 0.25,
                                            color: 'text.secondary',
                                            '&:hover': {
                                              color: 'error.main',
                                              bgcolor: theme => alpha(theme.palette.error.main, 0.1),
                                            }
                                          }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                        <TextField
                                          fullWidth
                                          placeholder={`Goal ${index + 1}`}
                                          value={goal.goal}
                                          onChange={(e) => handleUpdateGoal(goal.id, e.target.value)}
                                          multiline
                                          rows={2}
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                              bgcolor: goal.achieved ? 'rgba(0, 0, 0, 0.02)' : 'background.paper',
                                              ...(goal.achieved && {
                                                '& .MuiOutlinedInput-input': {
                                                  textDecoration: 'line-through',
                                                  color: 'text.secondary',
                                                }
                                              })
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                                {/* Add Goal Button Card */}
                                <Grid item xs={12} sm={6} md={4}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 0,
                                      height: '100%',
                                      minHeight: 120,
                                      border: '2px dashed',
                                      borderColor: 'primary.main',
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                                      transition: 'all 0.2s ease',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                        borderColor: 'primary.dark',
                                        transform: 'translateY(-2px)',
                                        boxShadow: 2,
                                      },
                                    }}
                                    onClick={handleAddGoal}
                                  >
                                    <Button
                                      variant="text"
                                      startIcon={<AddIcon />}
                                      onClick={handleAddGoal}
                                      sx={{
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        '&:hover': {
                                          bgcolor: 'transparent',
                                        }
                                      }}
                                    >
                                      Add Goal
                                    </Button>
                                  </Paper>
                                </Grid>
                              </Grid>
                            ) : (
                              <Box sx={{ 
                                p: 3, 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                                borderRadius: 1.5,
                                border: '1px dashed',
                                borderColor: 'divider',
                                gap: 1
                              }}>
                                <FlagIcon color="disabled" sx={{ fontSize: '2rem', opacity: 0.4 }} />
                                <Typography color="text.secondary" variant="body2" textAlign="center">
                                  No goals added yet. Click below to add your first goal.
                                </Typography>
                                <Button
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  size="small"
                                  onClick={handleAddGoal}
                                  sx={{ 
                                    mt: 1,
                                    borderRadius: 1.5, 
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                      borderStyle: 'solid',
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                    }
                                  }}
                                >
                                  Add Goal
                                </Button>
                              </Box>
                            )}
                            
                          </Box>
                        ) : (
                          // View Mode - Multiple Goals UI
                          <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column', 
                            gap: 2,
                          }}>
                            {/* Progress Summary Card */}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: theme => alpha(theme.palette.primary.main, 0.1),
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1.5
                              }}>
                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                  Progress
                                </Typography>
                                <Typography variant="body1" color="primary.main" sx={{ fontWeight: 700 }}>
                                  {Math.round((formData.weeklyGoals.filter(g => g.achieved).length / formData.weeklyGoals.length) * 100)}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={(formData.weeklyGoals.filter(g => g.achieved).length / formData.weeklyGoals.length) * 100}
                                sx={{ 
                                  height: 10, 
                                  borderRadius: 5,
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 5,
                                    bgcolor: 'primary.main',
                                    background: theme => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                  }
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                                {formData.weeklyGoals.filter(g => g.achieved).length} of {formData.weeklyGoals.length} goals completed
                              </Typography>
                            </Paper>
                            
                            {/* Goal cards */}
                            <Grid container spacing={1.5}>
                              {[...formData.weeklyGoals]
                                .sort((a, b) => {
                                  if (a.achieved && !b.achieved) return 1;
                                  if (!a.achieved && b.achieved) return -1;
                                  return 0;
                                })
                                .map((goal) => (
                                <Grid item xs={12} sm={6} md={4} key={goal.id}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      height: '100%',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      border: '1px solid',
                                      borderColor: goal.achieved ? 'success.main' : theme => alpha(theme.palette.primary.main, 0.1),
                                      bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                      borderRadius: 1.5,
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      opacity: goal.achieved ? 0.9 : 1,
                                      '&:hover': {
                                        boxShadow: 2,
                                        transform: 'translateY(-2px)',
                                        borderColor: goal.achieved ? 'success.dark' : 'primary.main',
                                      },
                                    }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      gap: 1,
                                      flex: 1,
                                    }}>
                                      <Checkbox
                                        checked={goal.achieved}
                                        onChange={() => handleToggleGoalAchieved(goal.id)}
                                        color="success"
                                        size="small"
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography 
                                          sx={{ 
                                            fontWeight: goal.achieved ? 500 : 600,
                                            color: goal.achieved ? 'text.secondary' : 'text.primary',
                                            textDecoration: goal.achieved ? 'line-through' : 'none',
                                            transition: 'all 0.3s ease',
                                            lineHeight: 1.5,
                                            fontSize: '0.9375rem',
                                          }}
                                        >
                                          {goal.goal}
                                        </Typography>
                                      </Box>
                                      {goal.achieved && (
                                        <Chip 
                                          icon={<CheckCircleIcon />} 
                                          label="Achieved" 
                                          size="small"
                                          color="success"
                                          sx={{ 
                                            height: 24,
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            '& .MuiChip-icon': {
                                              fontSize: '0.875rem',
                                            }
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                  
                  {/* Modern Telephony Statistics */}
                  <Box sx={{ mb: 4 }}>
                    <Accordion
                      expanded={expandedSections.telephony}
                      onChange={(_, isExpanded) => setExpandedSections(prev => ({ ...prev, telephony: isExpanded }))}
                      sx={{
                        mb: 2,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        border: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: expandedSections.telephony ? '20px 20px 0 0' : '20px',
                          minHeight: 56,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '& .MuiAccordionSummary-content': {
                            my: 1.5,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <CallIcon />
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Telephony Statistics
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 4, 
                            borderRadius: '0 0 20px 20px',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            boxShadow: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderTop: 'none',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                            }
                          }}
                        >
                          <TableContainer 
                            component={Paper} 
                            sx={{ 
                              mb: 3,
                              borderRadius: 2,
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              border: '1px solid',
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Table size="large">
                              <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                                  <TableCell sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Day</TableCell>
                                  <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Received Calls</TableCell>
                                  <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Answered Calls</TableCell>
                                  {editMode && (
                                    <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Calls to Subtract</TableCell>
                                  )}
                                  <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Answer Rate</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(formData.telephonyStats)
                                  .sort(([a], [b]) => {
                                    const dayOrder = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5 };
                                    return dayOrder[a] - dayOrder[b];
                                  })
                                  .map(([day, stats]) => {
                                    const adjustedReceived = Math.max(0, Number(stats.receivedCalls) - Number(stats.subtractNumber));
                                    const adjustedAnswered = Math.max(0, Number(stats.answeredCalls) - Number(stats.subtractNumber));
                                    
                                    return (
                                      <TableRow key={day}>
                                        <TableCell component="th" scope="row" sx={{ ...modernTableStyles }}>
                                          {day.charAt(0).toUpperCase() + day.slice(1)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {editMode ? (
                                            <TextField
                                              type="text"
                                              inputMode="numeric"
                                              value={stats.receivedCalls}
                                              onChange={(e) => handleTelephonyChange(day, 'receivedCalls', e.target.value)}
                                              disabled={!editMode}
                                              variant="outlined"
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                                '& .MuiInputBase-input': {
                                                  fontSize: '1.1rem',
                                                }
                                              }}
                                              inputProps={{ 
                                                style: { 
                                                  textAlign: 'center',
                                                  fontWeight: 'bold',
                                                }
                                              }}
                                            />
                                          ) : (
                                            <Typography sx={{ ...modernTableStyles }}>{adjustedReceived}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="center">
                                          {editMode ? (
                                            <TextField
                                              type="text"
                                              inputMode="numeric"
                                              value={stats.answeredCalls}
                                              onChange={(e) => handleTelephonyChange(day, 'answeredCalls', e.target.value)}
                                              disabled={!editMode}
                                              variant="outlined"
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                                '& .MuiInputBase-input': {
                                                  fontSize: '1.1rem',
                                                }
                                              }}
                                              inputProps={{ 
                                                style: { 
                                                  textAlign: 'center',
                                                  fontWeight: 'bold',
                                                }
                                              }}
                                            />
                                          ) : (
                                            <Typography sx={{ ...modernTableStyles }}>{adjustedAnswered}</Typography>
                                          )}
                                        </TableCell>
                                        {editMode && (
                                          <TableCell align="center">
                                            <TextField
                                              type="text"
                                              inputMode="numeric"
                                              value={stats.subtractNumber}
                                              onChange={(e) => handleTelephonyChange(day, 'subtractNumber', e.target.value)}
                                              disabled={!editMode}
                                              variant="outlined"
                                              sx={{
                                                '& .MuiOutlinedInput-root': {
                                                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                                '& .MuiInputBase-input': {
                                                  fontSize: '1.1rem',
                                                }
                                              }}
                                              inputProps={{ 
                                                style: { 
                                                  textAlign: 'center',
                                                  fontWeight: 'bold',
                                                }
                                              }}
                                            />
                                          </TableCell>
                                        )}
                                        <TableCell align="center" sx={{ 
                                          color: Number(calculateCallPercentage(day)) === 0 ? 'text.secondary' :
                                                 Number(calculateCallPercentage(day)) >= 80 ? 'success.main' : 
                                                 Number(calculateCallPercentage(day)) >= 60 ? 'warning.main' : 'error.main',
                                          fontWeight: 'bold',
                                          ...modernTableStyles
                                        }}>
                                          {Number(calculateCallPercentage(day)) === 0 ? '-' : `${calculateCallPercentage(day)}%`}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                {/* Total Row */}
                                <TableRow>
                                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                                    TOTAL
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                                    {editMode 
                                      ? calculateTelephonyTotals().receivedCalls 
                                      : Math.max(0, calculateTelephonyTotals().receivedCalls - calculateTelephonyTotals().subtractNumber)
                                    }
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                                    {editMode 
                                      ? calculateTelephonyTotals().answeredCalls 
                                      : Math.max(0, calculateTelephonyTotals().answeredCalls - calculateTelephonyTotals().subtractNumber)
                                    }
                                  </TableCell>
                                  {editMode && (
                                    <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                                      {calculateTelephonyTotals().subtractNumber}
                                    </TableCell>
                                  )}
                                  <TableCell align="center" sx={{ 
                                    color: Number(calculateCallPercentage()) === 0 ? 'text.secondary' :
                                           Number(calculateCallPercentage()) >= 80 ? 'success.main' : 
                                           Number(calculateCallPercentage()) >= 60 ? 'warning.main' : 'error.main',
                                    fontWeight: 'bold',
                                    fontSize: '1.3rem'
                                  }}>
                                    {Number(calculateCallPercentage()) === 0 ? '-' : `${calculateCallPercentage()}%`}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                  
                  {/* Modern Ticket Statistics */}
                  <Box sx={{ mb: 4 }}>
                    <Accordion
                      expanded={expandedSections.tickets}
                      onChange={(_, isExpanded) => setExpandedSections(prev => ({ ...prev, tickets: isExpanded }))}
                      sx={{
                        mb: 2,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        border: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: 'secondary.main',
                          color: 'secondary.contrastText',
                          borderRadius: expandedSections.tickets ? '20px 20px 0 0' : '20px',
                          minHeight: 56,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'secondary.dark',
                          },
                          '& .MuiAccordionSummary-content': {
                            my: 1.5,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <AssignmentIcon />
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Ticket Statistics
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 4, 
                            borderRadius: '0 0 20px 20px',
                            bgcolor: 'secondary.main',
                            color: 'secondary.contrastText',
                            boxShadow: 'none',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderTop: 'none',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                            }
                          }}
                        >
                          
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Table size="large">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                            <TableCell sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Day</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>INC Received</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>INC Resolved</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>SR Received</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>SR Resolved</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Total Received</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Total Resolved</TableCell>
                            <TableCell align="center" sx={{ ...modernTableStyles, fontWeight: 'bold', fontSize: '1.15rem' }}>Difference</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(formData.ticketStats)
                            .sort(([a], [b]) => {
                              const dayOrder = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5 };
                              return dayOrder[a] - dayOrder[b];
                            })
                            .map(([day, stats]) => {
                              const dailyTotals = calculateDailyTicketTotals(day);
                              return (
                                <TableRow key={day}>
                                  <TableCell component="th" scope="row" sx={{ ...modernTableStyles }}>
                                  {day.charAt(0).toUpperCase() + day.slice(1)}
                                </TableCell>
                                <TableCell align="center">
                                  {editMode ? (
                                    <TextField
                                      type="text"
                                      inputMode="numeric"
                                      value={stats.incReceived}
                                      onChange={(e) => handleTicketChange(day, 'incReceived', e.target.value)}
                                      disabled={!editMode}
                                      variant="outlined"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: '1.1rem',
                                        }
                                      }}
                                      inputProps={{ 
                                        style: { 
                                          textAlign: 'center',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ ...modernTableStyles }}>{stats.incReceived}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  {editMode ? (
                                    <TextField
                                      type="text"
                                      inputMode="numeric"
                                      value={stats.incResolved}
                                      onChange={(e) => handleTicketChange(day, 'incResolved', e.target.value)}
                                      disabled={!editMode}
                                      variant="outlined"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: '1.1rem',
                                        }
                                      }}
                                      inputProps={{ 
                                        style: { 
                                          textAlign: 'center',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ ...modernTableStyles }}>{stats.incResolved}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  {editMode ? (
                                    <TextField
                                      type="text"
                                      inputMode="numeric"
                                      value={stats.srReceived}
                                      onChange={(e) => handleTicketChange(day, 'srReceived', e.target.value)}
                                      disabled={!editMode}
                                      variant="outlined"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: '1.1rem',
                                        }
                                      }}
                                      inputProps={{ 
                                        style: { 
                                          textAlign: 'center',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ ...modernTableStyles }}>{stats.srReceived}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  {editMode ? (
                                    <TextField
                                      type="text"
                                      inputMode="numeric"
                                      value={stats.srResolved}
                                      onChange={(e) => handleTicketChange(day, 'srResolved', e.target.value)}
                                      disabled={!editMode}
                                      variant="outlined"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '& .MuiInputBase-input': {
                                          fontSize: '1.1rem',
                                        }
                                      }}
                                      inputProps={{ 
                                        style: { 
                                          textAlign: 'center',
                                          fontWeight: 'bold',
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography sx={{ ...modernTableStyles }}>{stats.srResolved}</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center" sx={{ ...modernTableStyles }}>
                                  {dailyTotals.received}
                                </TableCell>
                                <TableCell align="center" sx={{ ...modernTableStyles }}>
                                  {dailyTotals.resolved}
                                </TableCell>
                                <TableCell align="center" sx={{ 
                                  fontWeight: 'bold',
                                  color: dailyTotals.difference === 0 ? 'text.primary' : 
                                         dailyTotals.difference < 0 ? 'success.main' : 'error.main',
                                  ...modernTableStyles
                                }}>
                                  {dailyTotals.difference === 0 ? '0' : 
                                   dailyTotals.difference < 0 ? dailyTotals.difference : 
                                   `+${dailyTotals.difference}`}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Total Row */}
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              TOTAL
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {ticketTotals.incReceived}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {ticketTotals.incResolved}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {ticketTotals.srReceived}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {ticketTotals.srResolved}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {totalReceived}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', ...modernTableStyles, fontSize: '1.2rem' }}>
                              {totalResolved}
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontWeight: 'bold',
                              color: backlogChange === 0 ? 'text.primary' : 
                                     backlogChange < 0 ? 'success.main' : 'error.main',
                              fontSize: '1.3rem'
                            }}>
                              {backlogChange === 0 ? '0' : 
                               backlogChange < 0 ? backlogChange : 
                               `+${backlogChange}`}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                        
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </Box>

                  {/* KPI Dashboard Cards */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <DashboardIcon color="primary" sx={{ fontSize: '1.5rem' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Key Performance Indicators
                      </Typography>
                    </Box>
                    {Object.values(formData.telephonyStats).every(day => 
                      day.receivedCalls === 0 && day.answeredCalls === 0
                    ) && Object.values(formData.ticketStats).every(day => 
                      day.incReceived === 0 && day.incResolved === 0 && day.srReceived === 0 && day.srResolved === 0
                    ) ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 4,
                          textAlign: 'center',
                          borderRadius: 3,
                          border: '1px dashed',
                          borderColor: 'divider',
                          bgcolor: 'background.default',
                        }}
                      >
                        <DashboardIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Data Available Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Enter statistics data to see your key performance indicators
                        </Typography>
                        {canEdit && (
                          <Button
                            variant="contained"
                            onClick={() => setEditMode(true)}
                            startIcon={<AddIcon />}
                            sx={{ mt: 1 }}
                          >
                            Start Adding Data
                          </Button>
                        )}
                      </Paper>
                    ) : (
                      <Grid container spacing={2}>
                      {/* Answer Rate KPI */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          elevation={0}
                          sx={(theme) => {
                            const currentAnswerRate = Number(calculateCallPercentage());
                            
                            return {
                              p: 3,
                              borderRadius: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                              transition: 'all 0.2s ease-in-out',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:hover': {
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                transform: 'translateY(-2px)',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                bgcolor: currentAnswerRate >= 90 ? 'success.main' : 
                                        currentAnswerRate >= 80 ? 'warning.main' : 'error.main',
                              }
                            };
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CallIcon sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Answer Rate
                              </Typography>
                            </Box>
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const comparison = prevTotals 
                                ? calculateComparison(currentTotals.telephony.answerRate, prevTotals.telephony.answerRate)
                                : { change: 0, percentage: 0, trend: 'none' };
                              
                              if (comparison.trend === 'none') return null;
                              
                              return (
                                <Chip
                                  icon={comparison.trend === 'up' ? <TrendingUpIcon /> : comparison.trend === 'down' ? <TrendingDownIcon /> : <RemoveIcon />}
                                  label={`${comparison.trend === 'up' ? '+' : '-'}${comparison.percentage}%`}
                                  color={comparison.trend === 'up' ? 'success' : 'error'}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </Box>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 0.5,
                              color: Number(calculateCallPercentage()) >= 90 ? 'success.main' : 
                                     Number(calculateCallPercentage()) >= 80 ? 'warning.main' : 'error.main'
                            }}
                          >
                            {Number(calculateCallPercentage()) === 0 ? '-' : `${calculateCallPercentage()}%`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              if (prevTotals && prevTotals.daysCompared) {
                                return `Comparing ${prevTotals.daysLabels.join(', ')}`;
                              }
                              return 'Target: 90%';
                            })()}
                          </Typography>
                          {benchmarks.answerRate.badge && (
                            <Chip
                              icon={<CheckCircleBadgeIcon />}
                              label="Target Met"
                              color="success"
                              size="small"
                              sx={{
                                mt: 1,
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Paper>
                      </Grid>

                      {/* Total Calls KPI */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                              transform: 'translateY(-2px)',
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              bgcolor: 'primary.main',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CallIcon sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Total Calls
                              </Typography>
                            </Box>
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const comparison = prevTotals 
                                ? calculateComparison(currentTotals.telephony.adjustedReceivedCalls, prevTotals.telephony.adjustedReceivedCalls)
                                : { change: 0, percentage: 0, trend: 'none' };
                              
                              if (comparison.trend === 'none') return null;
                              
                              return (
                                <Chip
                                  icon={comparison.trend === 'up' ? <TrendingUpIcon /> : comparison.trend === 'down' ? <TrendingDownIcon /> : <RemoveIcon />}
                                  label={`${comparison.trend === 'up' ? '+' : '-'}${comparison.percentage}%`}
                                  color={comparison.trend === 'up' ? 'success' : 'error'}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {(() => {
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              return currentTotals.telephony.adjustedReceivedCalls || 0;
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              if (prevTotals && prevTotals.daysCompared) {
                                return `Comparing ${prevTotals.daysLabels.join(', ')}`;
                              }
                              return 'Adjusted received calls';
                            })()}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Total Tickets KPI */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                              transform: 'translateY(-2px)',
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              bgcolor: 'secondary.main',
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AssignmentIcon sx={{ fontSize: '1.5rem', color: 'secondary.main' }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Total Tickets
                              </Typography>
                            </Box>
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const comparison = prevTotals 
                                ? calculateComparison(currentTotals.tickets.totalReceived, prevTotals.tickets.totalReceived)
                                : { change: 0, percentage: 0, trend: 'none' };
                              
                              if (comparison.trend === 'none') return null;
                              
                              return (
                                <Chip
                                  icon={comparison.trend === 'up' ? <TrendingUpIcon /> : comparison.trend === 'down' ? <TrendingDownIcon /> : <RemoveIcon />}
                                  label={`${comparison.trend === 'up' ? '+' : '-'}${comparison.percentage}%`}
                                  color={comparison.trend === 'up' ? 'success' : 'error'}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {(() => {
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              return currentTotals.tickets.totalReceived || 0;
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              if (prevTotals && prevTotals.daysCompared) {
                                return `Comparing ${prevTotals.daysLabels.join(', ')}`;
                              }
                              return 'Received this week';
                            })()}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Backlog Change KPI */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper
                          elevation={0}
                          sx={(theme) => ({
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                              transform: 'translateY(-2px)',
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '4px',
                              bgcolor: (() => {
                                const currentTotals = getCurrentWeekTotalsForComparison();
                                const backlog = currentTotals.tickets.backlogChange;
                                return backlog === 0 ? 'info.main' : 
                                       backlog < 0 ? 'success.main' : 'error.main';
                              })()
                            }
                          })}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AssignmentIcon sx={{ fontSize: '1.5rem', color: (() => {
                                const currentTotals = getCurrentWeekTotalsForComparison();
                                const backlog = currentTotals.tickets.backlogChange;
                                return backlog === 0 ? 'info.main' : backlog < 0 ? 'success.main' : 'error.main';
                              })() }} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Backlog Change
                              </Typography>
                            </Box>
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const comparison = prevTotals 
                                ? calculateComparison(currentTotals.tickets.backlogChange, prevTotals.tickets.backlogChange)
                                : { change: 0, percentage: 0, trend: 'none' };
                              
                              if (comparison.trend === 'none') return null;
                              
                              // For backlog change: decrease (more negative) is good, increase (more positive) is bad
                              // So we invert the colors: 'up' trend (more positive) is bad, 'down' trend (more negative) is good
                              const isGood = comparison.trend === 'down';
                              
                              return (
                                <Chip
                                  icon={isGood ? <TrendingDownIcon /> : <TrendingUpIcon />}
                                  label={`${comparison.trend === 'up' ? '+' : '-'}${comparison.percentage}%`}
                                  color={isGood ? 'success' : 'error'}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </Box>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 0.5,
                              color: backlogChange === 0 ? 'info.main' : 
                                     backlogChange < 0 ? 'success.main' : 'error.main'
                            }}
                          >
                            {(() => {
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const backlog = currentTotals.tickets.backlogChange;
                              return backlog === 0 ? '0' : 
                                     backlog < 0 ? backlog : 
                                     `+${backlog}`;
                            })()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(() => {
                              const prevTotals = getPreviousWeekTotals();
                              const currentTotals = getCurrentWeekTotalsForComparison();
                              const backlog = currentTotals.tickets.backlogChange;
                              if (prevTotals && prevTotals.daysCompared) {
                                return `Comparing ${prevTotals.daysLabels.join(', ')}`;
                              }
                              return backlog === 0 ? 'No change' : 
                                     backlog < 0 ? 'Backlog reduced' : 'Backlog increased';
                            })()}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}
                  </Box>

                  {/* Mini Daily Charts Section */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Daily Trends
                    </Typography>
                    {dailyTelephonyChartData.every(d => d.received === 0 && d.answered === 0) && 
                     dailyTicketChartData.every(d => d.received === 0 && d.resolved === 0) ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 6,
                          textAlign: 'center',
                          borderRadius: 3,
                          border: '1px dashed',
                          borderColor: 'divider',
                          bgcolor: 'background.default',
                        }}
                      >
                        <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Data Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Start entering data to see daily trends visualizations
                        </Typography>
                      </Paper>
                    ) : (
                      <Grid container spacing={2}>
                      {/* Telephony Daily Chart */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CallIcon sx={{ fontSize: '1.25rem', color: 'primary.main' }} />
                            Telephony Performance
                          </Typography>
                          <Box sx={{ height: 200 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={dailyTelephonyChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis 
                                  dataKey="day" 
                                  tick={{ fill: '#666', fontSize: 12 }}
                                  axisLine={{ stroke: '#ddd' }}
                                />
                                <YAxis 
                                  yAxisId="left"
                                  tick={{ fill: '#666', fontSize: 12 }}
                                  axisLine={{ stroke: '#ddd' }}
                                />
                                <YAxis 
                                  yAxisId="right"
                                  orientation="right"
                                  domain={[0, 100]}
                                  tick={{ fill: '#666', fontSize: 12 }}
                                  axisLine={{ stroke: '#ddd' }}
                                />
                                <Tooltip 
                                  contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                  }}
                                  formatter={(value, name) => {
                                    if (name === 'answerRate') return [`${value}%`, 'Answer Rate'];
                                    return [value, name === 'received' ? 'Received' : 'Answered'];
                                  }}
                                />
                                <Bar 
                                  yAxisId="left" 
                                  dataKey="received" 
                                  name="received"
                                  fill="#1A73E8"
                                  radius={[4, 4, 0, 0]}
                                  opacity={0.8}
                                />
                                <Bar 
                                  yAxisId="left" 
                                  dataKey="answered" 
                                  name="answered"
                                  fill="#34A853"
                                  radius={[4, 4, 0, 0]}
                                  opacity={0.8}
                                />
                                <Line 
                                  yAxisId="right"
                                  type="monotone" 
                                  dataKey="answerRate" 
                                  name="answerRate"
                                  stroke="#EA4335" 
                                  strokeWidth={2}
                                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Tickets Daily Chart */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssignmentIcon sx={{ fontSize: '1.25rem', color: 'secondary.main' }} />
                            Ticket Activity
                          </Typography>
                          <Box sx={{ height: 200 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={dailyTicketChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis 
                                  dataKey="day" 
                                  tick={{ fill: '#666', fontSize: 12 }}
                                  axisLine={{ stroke: '#ddd' }}
                                />
                                <YAxis 
                                  tick={{ fill: '#666', fontSize: 12 }}
                                  axisLine={{ stroke: '#ddd' }}
                                />
                                <Tooltip 
                                  contentStyle={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                  }}
                                  formatter={(value, name) => {
                                    return [`${value} tickets`, name === 'received' ? 'Received' : name === 'resolved' ? 'Resolved' : 'Difference'];
                                  }}
                                />
                                <Bar 
                                  dataKey="received" 
                                  name="received"
                                  fill="#1A73E8"
                                  radius={[4, 4, 0, 0]}
                                  opacity={0.8}
                                />
                                <Bar 
                                  dataKey="resolved" 
                                  name="resolved"
                                  fill="#34A853"
                                  radius={[4, 4, 0, 0]}
                                  opacity={0.8}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="difference" 
                                  name="difference"
                                  stroke="#EA4335" 
                                  strokeWidth={2}
                                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}
                  </Box>
                </>
              ) : (
                // Historical Charts View
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    <Typography variant="h5" component="h3">
                      Historical Trends
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {/* Historical View Controls */}
                      <Paper 
                        elevation={0}
                        sx={(theme) => ({ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1.5,
                          backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          border: '1px solid',
                          borderColor: alpha(theme.palette.primary.main, 0.15),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          }
                        })}
                      >
                      <Checkbox
                        checked={showAllWeeks}
                        onChange={(e) => setShowAllWeeks(e.target.checked)}
                        color="primary"
                        size="small"
                        sx={{ 
                          p: 0.5,
                          '& .MuiSvgIcon-root': { fontSize: 18 }
                        }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        Show all
                      </Typography>
                      
                      {!showAllWeeks && (
                        <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Last
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={weeksToShow}
                            onChange={(e) => setWeeksToShow(Math.max(1, parseInt(e.target.value) || 1))}
                            inputProps={{ 
                              min: 1, 
                              max: 52,
                              style: { textAlign: 'center', width: '40px', fontSize: '0.8rem' }
                            }}
                            sx={(theme) => ({
                              '& .MuiOutlinedInput-root': {
                                height: 24,
                                minHeight: 24,
                                fontSize: '0.8rem',
                                borderRadius: 1,
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.primary.main, 0.2),
                                },
                                '&:hover fieldset': {
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main,
                                  borderWidth: 1,
                                }
                              },
                              '& .MuiInputBase-input': {
                                p: 0.5,
                                fontSize: '0.8rem',
                              }
                            })}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            weeks
                          </Typography>
                        </>
                      )}
                    </Paper>
                    
                    {/* Export Button */}
                    <MuiTooltip title="Export filtered data to Excel">
                      <IconButton
                        onClick={handleExportHistoricalData}
                        disabled={filteredHistoricalData.length === 0}
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                          border: '1px solid',
                          borderColor: alpha(theme.palette.success.main, 0.2),
                          borderRadius: 1.5,
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.success.main, 0.15),
                            borderColor: alpha(theme.palette.success.main, 0.3),
                          },
                          '&.Mui-disabled': {
                            backgroundColor: alpha(theme.palette.grey[500], 0.1),
                            borderColor: alpha(theme.palette.grey[500], 0.2),
                            opacity: 0.6,
                          }
                        })}
                      >
                        <FileDownloadIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      </IconButton>
                    </MuiTooltip>
                  </Box>
                  </Box>
                  
                  <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Telephony Performance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Shows call volumes and answer rate percentage over time.
                    </Typography>
                    
                    <Box sx={{ height: 400, mb: 3 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={filteredHistoricalData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <YAxis 
                            yAxisId="left" 
                            orientation="left"
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <YAxis 
                            yAxisId="right" 
                            orientation="right" 
                            domain={[0, 100]} 
                            unit="%" 
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <Tooltip 
                            content={(props) => {
                              if (!props.active || !props.payload || props.payload.length === 0) return null;
                              const entry = filteredHistoricalData.find(d => d.week === props.label);
                              return (
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                  padding: '16px',
                                  fontSize: '14px',
                                }}>
                                  <div style={{
                                    color: '#333',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    marginBottom: '12px',
                                    borderBottom: '2px solid #eee',
                                    paddingBottom: '8px'
                                  }}>
                                    Week {props.label}
                                    {entry && entry.totalGoals > 0 && (
                                      <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'normal', color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#666' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `- ${entry.goalsAchieved}/${entry.totalGoals} goals achieved`}
                                      </span>
                                    )}
                                  </div>
                                  {entry && entry.totalGoals > 0 && (
                                    <div style={{
                                      padding: '8px',
                                      marginBottom: '8px',
                                      backgroundColor: entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                      borderRadius: '4px',
                                      border: `1px solid ${entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                                    }}>
                                      <div style={{ color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#ff9800', fontWeight: '600', fontSize: '13px' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `${entry.goalsAchieved} of ${entry.totalGoals} goals achieved`}
                                      </div>
                                    </div>
                                  )}
                                  {props.payload.map((payloadEntry, index) => {
                                    let displayValue = payloadEntry.value;
                                    let displayName = payloadEntry.name;
                                    if (payloadEntry.name === "Answer Rate") {
                                      displayValue = `${payloadEntry.value}%`;
                                    } else if (payloadEntry.name === "Received Calls") {
                                      displayValue = `${payloadEntry.value} calls`;
                                      displayName = "Total Received";
                                    } else if (payloadEntry.name === "Answered Calls") {
                                      displayValue = `${payloadEntry.value} calls`;
                                      displayName = "Total Answered";
                                    }
                                    return (
                                      <div key={index} style={{
                                        color: '#666',
                                        fontSize: '14px',
                                        padding: '6px 0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '16px'
                                      }}>
                                        <span>{displayName}:</span>
                                        <span style={{ fontWeight: '600' }}>{displayValue}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            }}
                          />
                          <Legend 
                            verticalAlign="top"
                            height={36}
                            iconSize={12}
                            iconType="circle"
                            wrapperStyle={{
                              paddingBottom: '20px',
                            }}
                            formatter={(value) => (
                              <span style={{ color: '#666', fontSize: '12px' }}>{value}</span>
                            )}
                          />
                          <Bar 
                            yAxisId="left" 
                            dataKey="adjustedReceivedCalls" 
                            name="Received Calls" 
                            fill="#1A73E8"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={50}
                            opacity={0.8}
                          />
                          <Bar 
                            yAxisId="left" 
                            dataKey="adjustedAnsweredCalls" 
                            name="Answered Calls" 
                            fill="#34A853"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={50}
                            opacity={0.8}
                          />
                          <Line 
                            yAxisId="right" 
                            type="monotone" 
                            dataKey="callPercentage" 
                            name="Answer Rate" 
                            stroke="#EA4335" 
                            strokeWidth={3}
                            dot={{ r: 6, strokeWidth: 2, fill: '#fff' }}
                            activeDot={{ r: 8, strokeWidth: 2, fill: '#EA4335' }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                  
                  <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Ticket Trends
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Shows the total number of tickets received and resolved each week.
                    </Typography>
                    
                    <Box sx={{ height: 400, mb: 3 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={filteredHistoricalData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="week"
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <YAxis 
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.98)',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                              padding: '12px'
                            }}
                            content={(props) => {
                              if (!props.active || !props.payload || props.payload.length === 0) return null;
                              const entry = filteredHistoricalData.find(d => d.week === props.label);
                              return (
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                  padding: '12px',
                                  fontSize: '14px',
                                }}>
                                  <div style={{
                                    color: '#333',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    marginBottom: '8px'
                                  }}>
                                    Week {props.label}
                                    {entry && entry.totalGoals > 0 && (
                                      <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 'normal', color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#666' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `- ${entry.goalsAchieved}/${entry.totalGoals} goals achieved`}
                                      </span>
                                    )}
                                  </div>
                                  {entry && entry.totalGoals > 0 && (
                                    <div style={{
                                      padding: '6px',
                                      marginBottom: '8px',
                                      backgroundColor: entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                      borderRadius: '4px',
                                      border: `1px solid ${entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                                    }}>
                                      <div style={{ color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#ff9800', fontWeight: '600', fontSize: '12px' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `${entry.goalsAchieved} of ${entry.totalGoals} goals achieved`}
                                      </div>
                                    </div>
                                  )}
                                  {props.payload.map((payloadEntry, index) => (
                                    <div key={index} style={{
                                      color: '#666',
                                      fontSize: '13px',
                                      padding: '4px 0',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      gap: '16px'
                                    }}>
                                      <span>{payloadEntry.name}:</span>
                                      <span style={{ fontWeight: '600' }}>{payloadEntry.value} tickets</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                            formatter={(value) => [`${value} tickets`]}
                            labelStyle={{ 
                              color: '#333',
                              fontWeight: 'bold',
                              fontSize: '14px',
                              marginBottom: '8px'
                            }}
                            itemStyle={{ 
                              color: '#666',
                              fontSize: '13px',
                              padding: '4px 0'
                            }}
                          />
                          <Legend 
                            verticalAlign="top"
                            height={36}
                            iconSize={12}
                            iconType="circle"
                            wrapperStyle={{
                              paddingBottom: '20px',
                            }}
                            formatter={(value) => (
                              <span style={{ color: '#666', fontSize: '12px' }}>{value}</span>
                            )}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="totalReceived" 
                            name="Total Received" 
                            stroke="#1A73E8" 
                            strokeWidth={3}
                            dot={{ r: 6, strokeWidth: 2, fill: '#fff' }}
                            activeDot={{ r: 8, strokeWidth: 2, fill: '#1A73E8' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="totalResolved" 
                            name="Total Resolved" 
                            stroke="#34A853" 
                            strokeWidth={3}
                            dot={{ r: 6, strokeWidth: 2, fill: '#fff' }}
                            activeDot={{ r: 8, strokeWidth: 2, fill: '#34A853' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                  
                  <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                    <Typography variant="h6" component="h4" gutterBottom>
                      Backlog Change
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Negative values (green) indicate more tickets resolved than received, reducing backlog. 
                      Positive values (red) indicate backlog growth.
                    </Typography>
                    
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={filteredHistoricalData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="week"
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <YAxis 
                            tick={{ fill: '#666', fontSize: 12 }}
                            axisLine={{ stroke: '#ddd' }}
                            tickLine={{ stroke: '#ddd' }}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                            }}
                            content={(props) => {
                              if (!props.active || !props.payload || props.payload.length === 0) return null;
                              const entry = filteredHistoricalData.find(d => d.week === props.label);
                              return (
                                <div style={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  borderRadius: '8px',
                                  border: '1px solid #ddd',
                                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                  padding: '12px',
                                  fontSize: '14px',
                                }}>
                                  <div style={{
                                    color: '#333',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    marginBottom: '8px'
                                  }}>
                                    Week {props.label}
                                    {entry && entry.totalGoals > 0 && (
                                      <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 'normal', color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#666' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `- ${entry.goalsAchieved}/${entry.totalGoals} goals achieved`}
                                      </span>
                                    )}
                                  </div>
                                  {entry && entry.totalGoals > 0 && (
                                    <div style={{
                                      padding: '6px',
                                      marginBottom: '8px',
                                      backgroundColor: entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                      borderRadius: '4px',
                                      border: `1px solid ${entry.goalsAchieved === entry.totalGoals ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                                    }}>
                                      <div style={{ color: entry.goalsAchieved === entry.totalGoals ? '#4caf50' : '#ff9800', fontWeight: '600', fontSize: '12px' }}>
                                        {entry.goalsAchieved === entry.totalGoals 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `${entry.goalsAchieved} of ${entry.totalGoals} goals achieved`}
                                      </div>
                                    </div>
                                  )}
                                  {props.payload.map((payloadEntry, index) => (
                                    <div key={index} style={{
                                      color: '#666',
                                      fontSize: '13px',
                                      padding: '4px 0',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      gap: '16px'
                                    }}>
                                      <span>{payloadEntry.value <= 0 ? 'Backlog Reduced' : 'Backlog Increased'}:</span>
                                      <span style={{ fontWeight: '600', color: payloadEntry.value <= 0 ? '#4caf50' : '#f44336' }}>
                                        {Math.abs(payloadEntry.value)} tickets
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                            formatter={(value, name) => {
                              return [`${value} tickets`, value <= 0 ? 'Backlog Reduced' : 'Backlog Increased'];
                            }}
                            labelStyle={{ color: '#666' }}
                          />
                          <Legend 
                            verticalAlign="top"
                            height={36}
                            iconSize={12}
                            iconType="circle"
                            wrapperStyle={{
                              paddingBottom: '20px',
                            }}
                          />
                          <Bar 
                            dataKey="backlogChange" 
                            name="Backlog Change"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={50}
                          >
                            {historicalData.map((entry, index) => {
                              const color = entry.backlogChange <= 0 ? 
                                { fill: '#4caf50', fillOpacity: 0.8 } : 
                                { fill: '#f44336', fillOpacity: 0.8 };
                              return (
                                <Cell 
                                  key={`cell-${index}`}
                                  {...color}
                                />
                              );
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                  
                  {/* Goals Achievement Section */}
                  {filteredHistoricalData.some(entry => entry.totalGoals > 0) && (
                    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                      <Typography variant="h6" component="h4" gutterBottom>
                        Goals Achievement
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Shows the percentage of goals achieved each week. Green bars indicate all goals achieved (100%), yellow/orange bars show partial achievement.
                      </Typography>
                      
                      <Box sx={{ height: 400 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={filteredHistoricalData
                              .filter(entry => entry.totalGoals > 0)
                              .map(entry => ({
                                ...entry,
                                goalsAchievementPercentage: Math.round((entry.goalsAchieved / entry.totalGoals) * 100)
                              }))}
                            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="week"
                              tick={{ fill: '#666', fontSize: 12 }}
                              axisLine={{ stroke: '#ddd' }}
                              tickLine={{ stroke: '#ddd' }}
                            />
                            <YAxis 
                              domain={[0, 100]}
                              unit="%"
                              tick={{ fill: '#666', fontSize: 12 }}
                              axisLine={{ stroke: '#ddd' }}
                              tickLine={{ stroke: '#ddd' }}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                              }}
                              content={(props) => {
                                if (!props.active || !props.payload || props.payload.length === 0) return null;
                                const entry = filteredHistoricalData.find(d => d.week === props.label);
                                if (!entry || entry.totalGoals === 0) return null;
                                const percentage = Math.round((entry.goalsAchieved / entry.totalGoals) * 100);
                                const allAchieved = entry.goalsAchieved === entry.totalGoals;
                                
                                return (
                                  <div style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    padding: '12px',
                                    fontSize: '14px',
                                  }}>
                                    <div style={{
                                      color: '#333',
                                      fontWeight: 'bold',
                                      fontSize: '14px',
                                      marginBottom: '8px'
                                    }}>
                                      Week {props.label}
                                    </div>
                                    <div style={{
                                      padding: '6px',
                                      marginBottom: '8px',
                                      backgroundColor: allAchieved ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                                      borderRadius: '4px',
                                      border: `1px solid ${allAchieved ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                                    }}>
                                      <div style={{ color: allAchieved ? '#4caf50' : '#ff9800', fontWeight: '600', fontSize: '12px' }}>
                                        {allAchieved 
                                          ? `✓ All ${entry.totalGoals} goals achieved` 
                                          : `${entry.goalsAchieved} of ${entry.totalGoals} goals achieved`}
                                      </div>
                                    </div>
                                    {props.payload.map((payloadEntry, index) => (
                                      <div key={index} style={{
                                        color: '#666',
                                        fontSize: '13px',
                                        padding: '4px 0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '16px'
                                      }}>
                                        <span>Goals Achieved:</span>
                                        <span style={{ fontWeight: '600', color: allAchieved ? '#4caf50' : '#ff9800' }}>
                                          {percentage}%
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }}
                              formatter={(value) => [`${value}%`, 'Goals Achieved']}
                              labelStyle={{ color: '#666' }}
                            />
                            <Legend 
                              verticalAlign="top"
                              height={36}
                              iconSize={12}
                              iconType="circle"
                              wrapperStyle={{
                                paddingBottom: '20px',
                              }}
                            />
                            <Bar 
                              dataKey="goalsAchievementPercentage"
                              name="Goals Achieved"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={50}
                            >
                              {filteredHistoricalData
                                .filter(entry => entry.totalGoals > 0)
                                .map((entry, index) => {
                                  const allAchieved = entry.goalsAchieved === entry.totalGoals;
                                  const color = allAchieved 
                                    ? { fill: '#4caf50', fillOpacity: 0.8 } 
                                    : { fill: '#ff9800', fillOpacity: 0.8 };
                                  return (
                                    <Cell 
                                      key={`cell-${index}`}
                                      {...color}
                                    />
                                  );
                                })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}
            </>
          )}
      </Box>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: 24,
            right: 24,
          }
        }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem',
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* CSV Upload Dialog */}
      <Dialog 
        open={csvDialogOpen} 
        onClose={handleCsvDialogClose}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            maxHeight: '90vh',
            height: 'auto'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: { md: '400px' },
          height: 'auto'
        }}>
          {/* Left section with visual background and title */}
          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'white',
            p: 3,
            width: { xs: '100%', md: '240px' },
            minHeight: { xs: 'auto', md: '400px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <Box>
              <Typography variant="h5" fontWeight="medium" mb={2}>
                Import Statistics
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Upload your CSV files to quickly update this week's statistics.
              </Typography>
            </Box>
            
            <Box sx={{ opacity: 0.8, mt: 4 }}>
              <UploadFileIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
              <Typography variant="body2" fontWeight="medium">
                Today is {new Date().toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                Data will be imported for{' '}
                <Box component="span" fontWeight="bold">
                  {getDayToUpdate(new Date().getDay())?.charAt(0).toUpperCase() + 
                   getDayToUpdate(new Date().getDay())?.slice(1) || 'N/A (weekend)'}
                </Box>
              </Typography>
            </Box>
          </Box>
          
          {/* Right section with content */}
          <Box sx={{ 
            flex: 1, 
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'auto', md: '400px' }
          }}>
            <DialogTitle sx={{ px: 0, pt: 0 }}>
              <Typography variant="h6" fontWeight="bold" component="div">
                Select Files to Import
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, overflow: 'visible', flex: 1 }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                You can select both file types at once. The system will automatically detect and process each file type.
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3
              }}>
                {/* Ticket Statistics Card */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    flex: 1, 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Ticket Statistics
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    CSV with Type, created, and resolved columns for Incidents and Service Requests
                  </Typography>
                </Paper>
                
                {/* Telephony Statistics Card */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    flex: 1, 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CallIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Telephony Statistics
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Queue Performance Overview with Received, Serviced, and totals subtraction data
                  </Typography>
                </Paper>
              </Box>
              
              <Box 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{ 
                  mt: 3, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 3,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: isDragOver ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                  border: '2px dashed',
                  borderColor: isDragOver ? 'primary.main' : 'divider',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    borderColor: 'primary.light'
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleCsvUpload}
                />
                <Button
                  variant="contained"
                  onClick={handleCsvFileSelect}
                  startIcon={<UploadFileIcon />}
                  size="large"
                  sx={{ 
                    px: 4,
                    py: 1,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  {isDragOver ? 'Drop Files Here' : 'Select CSV File(s)'}
                </Button>
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  {isDragOver ? (
                    <>
                      <Box component="span" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        Drop your CSV files here
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box component="span" sx={{ fontWeight: 'medium', color: 'info.main' }}>
                        Drag & Drop or Click:
                      </Box>
                      {' '}Drop CSV files here or click to browse. Hold Ctrl (or ⌘ on Mac) to select multiple files.
                    </>
                  )}
                </Typography>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 0, pt: 2, justifyContent: 'flex-end', borderTop: 'none' }}>
              <Button 
                onClick={handleCsvDialogClose}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  minWidth: '100px'
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default WeeklyStatistics; 